import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Layout from '../components/Layout';
import { api } from '../lib/api';

// Types
interface Blueprint {
  id: number;
  blueprintTypeId: number;
  maxProductionLimit: number;
  activities: any;
  metaLevel?: number;
  activity?: 'manufacturing' | 'reaction';
  type?: {
    id: number;
    name: any;
    iconId: number | null;
  };
}

interface BlueprintDetails extends Blueprint {
  materials: Array<{
    id: string;
    typeId: number;
    quantity: number;
    activity: string;
    type: {
      id: number;
      name: any;
      iconId: number | null;
      volume: number | null;
      basePrice: number | null;
    };
  }>;
  products: Array<{
    id: string;
    typeId: number;
    quantity: number;
    probability: number | null;
    activity: string;
    type: {
      id: number;
      name: any;
      iconId: number | null;
      volume: number | null;
      basePrice: number | null;
    };
  }>;
}

interface SolarSystem {
  id: number;
  nameEn: string;
  securityStatus: number | null;
  regionId: number | null;
  costIndices: {
    manufacturing?: number;
    reaction?: number;
    researching_time_efficiency?: number;
    researching_material_efficiency?: number;
    copying?: number;
    invention?: number;
  } | null;
  costIndicesUpdatedAt: string | null;
}

interface StructureType {
  id: number;
  nameEn: string;
  iconId: number | null;
}

interface Structure {
  id: string;
  name: string;
  solarSystemId: number;
  structureTypeId: number;
  materialRig: string;
  timeRig: string;
  reactionRig: string;
  facilityTax?: number;
  solarSystem: SolarSystem;
  structureType: StructureType;
  createdAt: string;
}

// Per-node settings for blueprint calculations
interface NodeSettings {
  structureId: string | null;
  runs: number;
  materialEfficiency: number;
  timeEfficiency: number;
  janicePriceType: 'buy' | 'split' | 'sell';
  materialRigOverride: string | null;
  reactionRigOverride: string | null;
  timeRigOverride: string | null;
  industrySkill: number;
  advancedIndustrySkill: number;
}

// Buildable material info from API
interface BuildableInfo {
  blueprintId: number;
  outputQuantity: number;
  activity: 'manufacturing' | 'reaction';
}

// Build decision for a material (keyed by materialTypeId within a node)
interface BuildDecision {
  mode: 'buy' | 'build';
  childNodeId?: string; // Reference to child node when mode is 'build'
}

// Tree node representing a blueprint in the build tree
interface TreeNode {
  id: string;
  blueprint: Blueprint | null;
  blueprintDetails: BlueprintDetails | null;
  children: TreeNode[];
  expanded: boolean;
  settings: NodeSettings;
  parentNodeId?: string; // ID of parent node (for child builds)
  parentMaterialTypeId?: number; // TypeId of material this node builds (for child builds)
  // For shared nodes (level 2+): multiple parents can reference the same node
  parentRefs?: Array<{ nodeId: string; materialTypeId: number }>;
  // For ghost/reference nodes that point to a shared defining node
  isGhostRef?: boolean;
  definingNodeId?: string; // ID of the original/defining node this ghost references
}

// Default settings for new nodes
const defaultNodeSettings: NodeSettings = {
  structureId: null,
  runs: 1,
  materialEfficiency: 0,
  timeEfficiency: 0,
  janicePriceType: 'buy',
  materialRigOverride: null,
  reactionRigOverride: null,
  timeRigOverride: null,
  industrySkill: 5,
  advancedIndustrySkill: 5,
};

function BuildTree() {
  // Tree state
  const [tree, setTree] = useState<TreeNode>({
    id: 'root',
    blueprint: null,
    blueprintDetails: null,
    children: [],
    expanded: true,
    settings: { ...defaultNodeSettings },
  });
  const [selectedNodeId, setSelectedNodeId] = useState<string>('root');

  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [addingToNodeId, setAddingToNodeId] = useState<string | null>(null);

  // Blueprint search state (for modal)
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Blueprint[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedModalBlueprint, setSelectedModalBlueprint] = useState<Blueprint | null>(null);

  // Structure state (global list of available structures)
  const [structures, setStructures] = useState<Structure[]>([]);

  // Prices state
  const [janicePrices, setJanicePrices] = useState<Record<number, { buy: number; split: number; sell: number }>>({});
  const [adjustedPrices, setAdjustedPrices] = useState<Record<number, number>>({});
  const [refreshingPrices, setRefreshingPrices] = useState(false);

  // Buildable materials cache (typeId -> buildable info)
  const [buildableCache, setBuildableCache] = useState<Record<number, BuildableInfo | null>>({});
  // Build decisions per node (nodeId -> { materialTypeId -> decision })
  const [buildDecisions, setBuildDecisions] = useState<Record<string, Record<number, BuildDecision>>>({});
  // Loading state for fetching sub-blueprints
  const [loadingSubBlueprints, setLoadingSubBlueprints] = useState<Set<string>>(new Set());
  // Shared nodes for level 2+ materials (typeId -> node)
  // These are nodes that can be referenced by multiple parent nodes
  const [sharedNodes, setSharedNodes] = useState<Record<number, TreeNode>>({});
  // Filter to hide shared/ghost nodes in the tree
  const [filterSharedNodes, setFilterSharedNodes] = useState(false);

  // Refs to access latest state in async functions
  const treeRef = useRef(tree);
  const buildableCacheRef = useRef(buildableCache);
  const sharedNodesRef = useRef(sharedNodes);
  useEffect(() => { treeRef.current = tree; }, [tree]);
  useEffect(() => { buildableCacheRef.current = buildableCache; }, [buildableCache]);
  useEffect(() => { sharedNodesRef.current = sharedNodes; }, [sharedNodes]);

  // Fetch structures on mount
  useEffect(() => {
    const fetchStructures = async () => {
      try {
        const response = await api.get('/structures');
        setStructures(response.data);
      } catch (error) {
        console.error('Failed to fetch structures:', error);
      }
    };
    fetchStructures();
  }, []);

  // Fetch adjusted prices on mount
  useEffect(() => {
    const fetchAdjustedPrices = async () => {
      try {
        const response = await api.get('/market-prices/adjusted-prices');
        setAdjustedPrices(response.data.prices);
      } catch (error) {
        console.error('Failed to fetch adjusted prices:', error);
      }
    };
    fetchAdjustedPrices();
  }, []);

  // Helper functions for material calculations
  const getFacilityMaterialModifier = (facilityType: 'NPC' | 'Medium' | 'Large' | 'X-Large'): number => {
    switch (facilityType) {
      case 'Medium': return 0.01;
      case 'Large': return 0.02;
      case 'X-Large': return 0.01;
      default: return 0;
    }
  };

  const getRigsMaterialModifier = (rigsType: 'None' | 'T1' | 'T2', systemType: 'High' | 'Low' | 'Null'): number => {
    const baseModifiers = { None: 0, T1: 0.02, T2: 0.024 };
    const systemMultipliers = { High: 1, Low: 1.9, Null: 2.1 };
    return baseModifiers[rigsType] * systemMultipliers[systemType];
  };

  const calculateMaterialQuantity = (
    baseQuantity: number,
    me: number,
    numRuns: number,
    facilityType: 'NPC' | 'Medium' | 'Large' | 'X-Large',
    rigsType: 'None' | 'T1' | 'T2',
    systemType: 'High' | 'Low' | 'Null',
    isReactionCalc: boolean = false
  ): number => {
    if (isReactionCalc) {
      const rigsModifier = 1 - getRigsMaterialModifier(rigsType, systemType);
      const rounded = Math.round(baseQuantity * numRuns * rigsModifier * 100) / 100;
      return Math.ceil(rounded);
    }
    const meModifier = 1 - (me / 100);
    const facilityModifier = 1 - getFacilityMaterialModifier(facilityType);
    const rigsModifier = 1 - getRigsMaterialModifier(rigsType, systemType);
    const rounded = Math.round(baseQuantity * numRuns * meModifier * facilityModifier * rigsModifier * 100) / 100;
    return Math.ceil(rounded);
  };

  const getJanicePrice = (typeId: number, priceType: 'buy' | 'split' | 'sell'): number | null => {
    const prices = janicePrices[typeId];
    if (!prices) return null;
    return prices[priceType];
  };

  // Time calculation helper functions
  const getFacilityTimeModifier = (facilityType: 'NPC' | 'Medium' | 'Large' | 'X-Large'): number => {
    switch (facilityType) {
      case 'NPC': return 0;
      case 'Medium': return 0.15;
      case 'Large': return 0.20;
      case 'X-Large': return 0.30;
    }
  };

  const getRigsTimeModifier = (rigsType: 'None' | 'T1' | 'T2', systemType: 'High' | 'Low' | 'Null'): number => {
    const systemMultipliers = { High: 1, Low: 1.9, Null: 2.1 };
    const baseModifiers = { None: 0, T1: 0.20, T2: 0.24 };
    return baseModifiers[rigsType] * systemMultipliers[systemType];
  };

  const calculateProductionTime = (
    baseTime: number,
    te: number,
    industry: number,
    advancedIndustry: number,
    facilityType: 'NPC' | 'Medium' | 'Large' | 'X-Large',
    rigsType: 'None' | 'T1' | 'T2',
    systemType: 'High' | 'Low' | 'Null'
  ): number => {
    const timeModifier = 1 - (te * 0.02);
    const skillModifier = 1 - (industry * 0.04) - (advancedIndustry * 0.03);
    const facilityModifier = 1 - getFacilityTimeModifier(facilityType);
    const rigsModifier = 1 - getRigsTimeModifier(rigsType, systemType);
    return Math.round(baseTime * timeModifier * skillModifier * facilityModifier * rigsModifier);
  };

  // Format seconds to DD:HH:MM:SS (shows days when >= 24 hours)
  const formatDuration = (seconds: number): string => {
    const days = Math.floor(seconds / 86400);
    const hrs = Math.floor((seconds % 86400) / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    if (days > 0) {
      return `${days}d ${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Fetch buildable info for materials
  const fetchBuildableInfo = useCallback(async (details: BlueprintDetails | null) => {
    if (!details) return;

    const activityType = details.activity || 'manufacturing';
    const materials = details.materials.filter(m => m.activity === activityType);
    const typeIds = materials.map(m => m.typeId);

    if (typeIds.length === 0) return;

    // Filter out typeIds we already have in cache
    const uncachedTypeIds = typeIds.filter(id => buildableCache[id] === undefined);
    if (uncachedTypeIds.length === 0) return;

    try {
      const response = await api.post('/blueprints/check-buildable', { typeIds: uncachedTypeIds });
      const buildableData = response.data.buildable as Record<number, BuildableInfo | undefined>;

      // Update cache - set null for types that are not buildable
      const newCache: Record<number, BuildableInfo | null> = {};
      uncachedTypeIds.forEach(typeId => {
        newCache[typeId] = buildableData[typeId] || null;
      });

      setBuildableCache(prev => ({ ...prev, ...newCache }));
    } catch (error) {
      console.error('Failed to fetch buildable info:', error);
    }
  }, [buildableCache]);

  // Fetch Janice prices for a node's materials
  const fetchJanicePrices = useCallback(async (details: BlueprintDetails | null) => {
    if (!details) return;

    const activityType = details.activity || 'manufacturing';
    const materials = details.materials.filter(m => m.activity === activityType);
    const products = details.products.filter(p => p.activity === activityType);

    const typeIds = [
      ...materials.map(m => m.typeId),
      ...products.map(p => p.typeId),
    ];

    if (typeIds.length === 0) return;

    try {
      setRefreshingPrices(true);
      const response = await api.post('/janice-prices/fetch', { typeIds });

      // Transform the response format from priceBuy/priceSplit/priceSell to buy/split/sell
      const transformedPrices: Record<number, { buy: number; split: number; sell: number }> = {};
      const pricesData = response.data.prices;

      for (const typeId in pricesData) {
        const priceInfo = pricesData[typeId];
        transformedPrices[Number(typeId)] = {
          buy: priceInfo.priceBuy ?? 0,
          split: priceInfo.priceSplit ?? 0,
          sell: priceInfo.priceSell ?? 0,
        };
      }

      setJanicePrices(prev => ({ ...prev, ...transformedPrices }));
    } catch (error) {
      console.error('Failed to fetch Janice prices:', error);
    } finally {
      setRefreshingPrices(false);
    }
  }, []);

  // Search blueprints for modal
  const searchBlueprints = useCallback(async (query: string) => {
    if (query.length < 3 && query.length > 0) {
      setSearchResults([]);
      return;
    }

    try {
      setSearchLoading(true);
      const response = await api.get('/blueprints', {
        params: { search: query, limit: query ? 100 : 20 },
      });
      setSearchResults(response.data);
    } catch (error) {
      console.error('Failed to search blueprints:', error);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length >= 3 || searchQuery.length === 0) {
        searchBlueprints(searchQuery);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, searchBlueprints]);

  // Fetch blueprint details
  const fetchBlueprintDetails = async (blueprint: Blueprint): Promise<BlueprintDetails | null> => {
    try {
      const params = blueprint.activity ? { activity: blueprint.activity } : {};
      const response = await api.get(`/blueprints/${blueprint.id}`, { params });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch blueprint details:', error);
      return null;
    }
  };

  // Get selected node - use useMemo to properly react to tree/selectedNodeId changes
  const selectedNode = useMemo((): TreeNode | null => {
    if (selectedNodeId === 'root') return tree;

    const findNode = (node: TreeNode): TreeNode | null => {
      if (node.id === selectedNodeId) return node;
      for (const child of node.children) {
        const found = findNode(child);
        if (found) return found;
      }
      return null;
    };
    return findNode(tree);
  }, [tree, selectedNodeId]);

  // Helper function to calculate the depth of a node in the tree
  const getNodeDepth = useCallback((nodeId: string): number => {
    if (nodeId === 'root') return 0;

    const findDepth = (node: TreeNode, currentDepth: number): number | null => {
      if (node.id === nodeId) return currentDepth;
      for (const child of node.children) {
        const found = findDepth(child, currentDepth + 1);
        if (found !== null) return found;
      }
      return null;
    };
    return findDepth(tree, 0) ?? 0;
  }, [tree]);

  // Helper function to find a node in the tree by ID
  const findNodeById = useCallback((nodeId: string): TreeNode | null => {
    if (nodeId === 'root') return tree;

    const findNode = (node: TreeNode): TreeNode | null => {
      if (node.id === nodeId) return node;
      for (const child of node.children) {
        const found = findNode(child);
        if (found) return found;
      }
      return null;
    };
    return findNode(tree);
  }, [tree]);

  // Handle switching between Buy and Build modes for a material
  // For level 2+ materials (depth >= 2), we use shared nodes so the same material
  // built in multiple places shares the same settings and tree node
  const handleBuildModeChange = useCallback(async (
    nodeId: string,
    materialTypeId: number,
    mode: 'buy' | 'build',
    buildableInfo: BuildableInfo | null
  ) => {
    const currentDecision = buildDecisions[nodeId]?.[materialTypeId];
    const nodeDepth = getNodeDepth(nodeId);
    const isSharedLevel = nodeDepth >= 1; // Children of level 1 nodes (depth 1) become level 2+ nodes

    if (mode === 'buy') {
      // Switch to Buy mode
      const sharedNode = isSharedLevel ? sharedNodes[materialTypeId] : null;
      const childNodeId = currentDecision?.childNodeId;
      const isDefiningNode = sharedNode?.id === childNodeId;

      if (sharedNode) {
        // This is related to a shared node
        const currentRefs = sharedNode.parentRefs || [];
        const newRefs = currentRefs.filter(ref => ref.nodeId !== nodeId);

        // Always remove the child node from this parent (ghost or defining)
        if (childNodeId) {
          setTree(prevTree => {
            const removeFromParent = (node: TreeNode): TreeNode => {
              if (node.id === nodeId) {
                return {
                  ...node,
                  children: node.children.filter(child => child.id !== childNodeId),
                };
              }
              return { ...node, children: node.children.map(removeFromParent) };
            };
            return removeFromParent(prevTree);
          });
        }

        if (newRefs.length === 0) {
          // No more parents reference this - remove from shared registry
          // Also remove the defining node from tree if we were the definer
          setSharedNodes(prev => {
            const { [materialTypeId]: _, ...rest } = prev;
            return rest;
          });

          // If this wasn't our node but we're the last ref, remove defining node too
          if (!isDefiningNode && sharedNode.parentNodeId) {
            setTree(prevTree => {
              const removeDefiningNode = (node: TreeNode): TreeNode => {
                return {
                  ...node,
                  children: node.children
                    .filter(child => child.id !== sharedNode.id)
                    .map(removeDefiningNode),
                };
              };
              return removeDefiningNode(prevTree);
            });
          }
        } else if (isDefiningNode && newRefs.length > 0) {
          // We're removing the defining node but there are still ghost refs
          // Need to promote the first ghost to become the new defining node
          const newDefinerRef = newRefs[0];
          const newDefinerParentId = newDefinerRef.nodeId;
          const ghostIdToPromote = `ghost-${materialTypeId}-${newDefinerParentId}`;

          setTree(prevTree => {
            const promoteGhost = (node: TreeNode): TreeNode => {
              const updatedChildren = node.children.map(child => {
                if (child.id === ghostIdToPromote) {
                  // Promote this ghost to be the defining node
                  return {
                    ...child,
                    id: `shared-${materialTypeId}`,
                    isGhostRef: undefined,
                    definingNodeId: undefined,
                    children: sharedNode.children, // Inherit children from old defining node
                    expanded: sharedNode.expanded,
                  };
                }
                return promoteGhost(child);
              });
              return { ...node, children: updatedChildren };
            };
            return promoteGhost(prevTree);
          });

          // Update shared nodes registry with new defining node info
          setSharedNodes(prev => ({
            ...prev,
            [materialTypeId]: {
              ...prev[materialTypeId],
              id: `shared-${materialTypeId}`,
              parentNodeId: newDefinerParentId,
              parentRefs: newRefs,
              isGhostRef: undefined,
              definingNodeId: undefined,
            },
          }));

          // Update build decision for the promoted node
          setBuildDecisions(prev => ({
            ...prev,
            [newDefinerParentId]: {
              ...prev[newDefinerParentId],
              [materialTypeId]: { mode: 'build', childNodeId: `shared-${materialTypeId}` },
            },
          }));
        } else {
          // Just update parentRefs
          setSharedNodes(prev => ({
            ...prev,
            [materialTypeId]: {
              ...prev[materialTypeId],
              parentRefs: newRefs,
            },
          }));
        }
      } else if (childNodeId) {
        // Non-shared node - remove from tree
        setTree(prevTree => {
          const removeFromParent = (node: TreeNode): TreeNode => {
            if (node.id === nodeId) {
              return {
                ...node,
                children: node.children.filter(child => child.id !== childNodeId),
              };
            }
            return { ...node, children: node.children.map(removeFromParent) };
          };
          return removeFromParent(prevTree);
        });
      }

      // Update decision to buy
      setBuildDecisions(prev => ({
        ...prev,
        [nodeId]: {
          ...prev[nodeId],
          [materialTypeId]: { mode: 'buy' },
        },
      }));
    } else {
      // Switch to Build mode
      if (!buildableInfo) return;

      // Check if a shared node for this typeId already exists (for level 2+)
      const existingSharedNode = isSharedLevel ? sharedNodes[materialTypeId] : null;

      if (existingSharedNode) {
        // Shared node already exists - create a ghost reference node
        const ghostNodeId = `ghost-${materialTypeId}-${nodeId}`;

        // Create ghost node that references the defining node
        const ghostNode: TreeNode = {
          id: ghostNodeId,
          blueprint: existingSharedNode.blueprint,
          blueprintDetails: existingSharedNode.blueprintDetails,
          children: [], // Ghost nodes don't have their own children
          expanded: false,
          settings: existingSharedNode.settings, // Reference same settings
          parentNodeId: nodeId,
          parentMaterialTypeId: materialTypeId,
          isGhostRef: true,
          definingNodeId: existingSharedNode.id,
        };

        // Add ghost node to the parent's children
        setTree(prevTree => {
          const addGhostToNode = (node: TreeNode): TreeNode => {
            if (node.id === nodeId) {
              return { ...node, children: [...node.children, ghostNode] };
            }
            return { ...node, children: node.children.map(addGhostToNode) };
          };
          return addGhostToNode(prevTree);
        });

        // Update shared nodes registry with new parent ref
        setSharedNodes(prev => ({
          ...prev,
          [materialTypeId]: {
            ...prev[materialTypeId],
            parentRefs: [
              ...(prev[materialTypeId].parentRefs || []),
              { nodeId, materialTypeId },
            ],
          },
        }));

        // Update build decision to reference the ghost node
        setBuildDecisions(prev => ({
          ...prev,
          [nodeId]: {
            ...prev[nodeId],
            [materialTypeId]: { mode: 'build', childNodeId: ghostNodeId },
          },
        }));

        return; // Done - no API call needed
      }

      // No existing shared node - create a new one
      const loadingKey = `${nodeId}-${materialTypeId}`;
      setLoadingSubBlueprints(prev => new Set(prev).add(loadingKey));

      try {
        // Fetch the blueprint details for this material
        const response = await api.get(`/blueprints/by-product/${materialTypeId}`);
        const data = response.data;

        if (!data.exists) {
          console.error('Blueprint not found for typeId:', materialTypeId);
          return;
        }

        const newNodeId = isSharedLevel
          ? `shared-${materialTypeId}`
          : `node-${Date.now()}-${materialTypeId}`;

        // Get the current node to inherit structure settings
        const parentNode = findNodeById(nodeId);

        // Create new tree node
        const newNode: TreeNode = {
          id: newNodeId,
          blueprint: {
            id: data.blueprintId,
            blueprintTypeId: data.blueprint.blueprintTypeId,
            maxProductionLimit: data.blueprint.maxProductionLimit,
            activities: data.blueprint.activities,
            metaLevel: data.activity === 'reaction' ? 1 : 0,
            activity: data.activity,
            type: data.blueprint.products[0]?.type || null,
          },
          blueprintDetails: {
            ...data.blueprint,
            activity: data.activity,
          },
          children: [],
          expanded: true,
          settings: {
            ...defaultNodeSettings,
            // Inherit structure from parent
            structureId: parentNode?.settings.structureId || null,
            // Runs will be calculated based on required quantity
            runs: 1,
          },
          parentNodeId: nodeId,
          parentMaterialTypeId: materialTypeId,
          parentRefs: isSharedLevel ? [{ nodeId, materialTypeId }] : undefined,
        };

        // Add child node to the parent node in tree
        setTree(prevTree => {
          const addChildToNode = (node: TreeNode): TreeNode => {
            if (node.id === nodeId) {
              return { ...node, children: [...node.children, newNode] };
            }
            return { ...node, children: node.children.map(addChildToNode) };
          };
          return addChildToNode(prevTree);
        });

        // If this is a shared level, add to shared nodes registry
        if (isSharedLevel) {
          setSharedNodes(prev => ({
            ...prev,
            [materialTypeId]: newNode,
          }));
        }

        // Update build decision
        setBuildDecisions(prev => ({
          ...prev,
          [nodeId]: {
            ...prev[nodeId],
            [materialTypeId]: { mode: 'build', childNodeId: newNodeId },
          },
        }));

        // Fetch Janice prices for the new node's materials
        if (newNode.blueprintDetails) {
          fetchJanicePrices(newNode.blueprintDetails);
          fetchBuildableInfo(newNode.blueprintDetails);
        }
      } catch (error) {
        console.error('Failed to fetch sub-blueprint:', error);
      } finally {
        setLoadingSubBlueprints(prev => {
          const next = new Set(prev);
          next.delete(loadingKey);
          return next;
        });
      }
    }
  }, [buildDecisions, sharedNodes, getNodeDepth, findNodeById, fetchJanicePrices, fetchBuildableInfo]);

  // Handle add button click
  const handleAddClick = (nodeId: string) => {
    setAddingToNodeId(nodeId);
    setShowAddModal(true);
    setSearchQuery('');
    setSearchResults([]);
    setSelectedModalBlueprint(null);
    // Load initial blueprints
    searchBlueprints('');
  };

  // Handle create node
  const handleCreateNode = async () => {
    if (!selectedModalBlueprint || !addingToNodeId) return;

    const details = await fetchBlueprintDetails(selectedModalBlueprint);

    const newNode: TreeNode = {
      id: `node-${Date.now()}`,
      blueprint: selectedModalBlueprint,
      blueprintDetails: details,
      children: [],
      expanded: true,
      settings: { ...defaultNodeSettings },
    };

    // Add to tree
    setTree(prevTree => {
      const addToNode = (node: TreeNode): TreeNode => {
        if (node.id === addingToNodeId) {
          return { ...node, children: [...node.children, newNode] };
        }
        return { ...node, children: node.children.map(addToNode) };
      };
      return addToNode(prevTree);
    });

    // Fetch Janice prices for the new node
    if (details) {
      fetchJanicePrices(details);
    }

    // Select the new node
    setSelectedNodeId(newNode.id);
    setShowAddModal(false);
    setAddingToNodeId(null);
  };

  // Update settings for a specific node
  const updateNodeSettings = (nodeId: string, updates: Partial<NodeSettings>) => {
    setTree(prevTree => {
      const updateNode = (node: TreeNode): TreeNode => {
        if (node.id === nodeId) {
          return { ...node, settings: { ...node.settings, ...updates } };
        }
        return { ...node, children: node.children.map(updateNode) };
      };
      return updateNode(prevTree);
    });

    // If this is a shared node (defining node), also update sharedNodes registry
    // so ghost nodes get the updated settings
    if (nodeId.startsWith('shared-')) {
      // Extract materialTypeId from nodeId (format: shared-{materialTypeId})
      const materialTypeId = parseInt(nodeId.replace('shared-', ''));
      if (!isNaN(materialTypeId) && sharedNodes[materialTypeId]) {
        setSharedNodes(prev => ({
          ...prev,
          [materialTypeId]: {
            ...prev[materialTypeId],
            settings: { ...prev[materialTypeId].settings, ...updates },
          },
        }));
      }
    }
  };

  // Fetch Janice prices and buildable info when selected node changes
  useEffect(() => {
    if (selectedNode?.blueprintDetails) {
      fetchJanicePrices(selectedNode.blueprintDetails);
      fetchBuildableInfo(selectedNode.blueprintDetails);
    }
  }, [selectedNodeId, selectedNode?.blueprintDetails, fetchJanicePrices, fetchBuildableInfo]);

  // Calculate the cost PER UNIT for a child node (for use in parent's material price)
  // Returns the cost to produce one unit of the output product
  const calculateChildNodeCostPerUnit = useCallback((childNodeId: string): number | null => {
    let childNode = findNodeById(childNodeId);
    if (!childNode?.blueprintDetails) return null;

    // If this is a ghost node, use the defining node's data from sharedNodes
    let effectiveNodeId = childNodeId;
    if (childNode.isGhostRef && childNode.parentMaterialTypeId) {
      const definingNode = sharedNodes[childNode.parentMaterialTypeId];
      if (definingNode) {
        childNode = definingNode;
        effectiveNodeId = definingNode.id;
      }
    }

    const details = childNode.blueprintDetails;
    if (!details) return null;

    const activityType = childNode.blueprint?.activity || 'manufacturing';
    const materials = details.materials.filter(m => m.activity === activityType);
    const products = details.products.filter(p => p.activity === activityType);
    const nodeSettings = childNode.settings;

    // Get output quantity per run
    const outputQuantityPerRun = products[0]?.quantity ?? 1;
    const totalOutputQuantity = outputQuantityPerRun * nodeSettings.runs;

    // Get facility/rig settings for this child node
    const childStructure = structures.find(s => s.id === nodeSettings.structureId) ?? null;

    const getChildFacilityType = (): 'NPC' | 'Medium' | 'Large' | 'X-Large' => {
      if (!childStructure) return 'NPC';
      const typeId = childStructure.structureTypeId;
      if (typeId === 35825) return 'Medium';
      if (typeId === 35826) return 'Large';
      if (typeId === 35827) return 'X-Large';
      return 'NPC';
    };

    const getChildSystemType = (): 'High' | 'Low' | 'Null' => {
      if (!childStructure) return 'High';
      const sec = childStructure.solarSystem.securityStatus;
      if (sec === null) return 'High';
      if (sec >= 0.5) return 'High';
      if (sec > 0.0) return 'Low';
      return 'Null';
    };

    const getChildMaterialRig = (): 'None' | 'T1' | 'T2' => {
      if (nodeSettings.materialRigOverride) return nodeSettings.materialRigOverride as 'None' | 'T1' | 'T2';
      if (!childStructure) return 'None';
      return (childStructure.materialRig || 'None') as 'None' | 'T1' | 'T2';
    };

    const getChildReactionRig = (): 'None' | 'T1' | 'T2' => {
      if (nodeSettings.reactionRigOverride) return nodeSettings.reactionRigOverride as 'None' | 'T1' | 'T2';
      if (!childStructure) return 'None';
      return (childStructure.reactionRig || 'None') as 'None' | 'T1' | 'T2';
    };

    const childFacility = getChildFacilityType();
    const childSystem = getChildSystemType();
    const isChildReaction = activityType === 'reaction';
    const childEffectiveRig = isChildReaction ? getChildReactionRig() : getChildMaterialRig();

    // Calculate material costs (recursively checking for nested builds)
    let totalMaterialCost = 0;
    materials.forEach(m => {
      const qty = calculateMaterialQuantity(
        m.quantity,
        nodeSettings.materialEfficiency,
        nodeSettings.runs,
        childFacility,
        childEffectiveRig,
        childSystem,
        isChildReaction
      );

      // Check if this material is also being built (use effectiveNodeId for shared nodes)
      const materialDecision = buildDecisions[effectiveNodeId]?.[m.typeId];
      if (materialDecision?.mode === 'build' && materialDecision.childNodeId) {
        // Recursively calculate the child's cost per unit, then multiply by quantity needed
        const nestedCostPerUnit = calculateChildNodeCostPerUnit(materialDecision.childNodeId);
        if (nestedCostPerUnit !== null) {
          totalMaterialCost += nestedCostPerUnit * qty;
        }
      } else {
        // Use Janice price
        const price = getJanicePrice(m.typeId, nodeSettings.janicePriceType);
        totalMaterialCost += (price || 0) * qty;
      }
    });

    // Calculate job costs (EIV-based)
    const eiv = materials.reduce((sum, m) =>
      sum + (adjustedPrices[m.typeId] ? adjustedPrices[m.typeId] * m.quantity * nodeSettings.runs : 0), 0
    );

    const getStructureRoleBonus = (): number => {
      if (!childStructure) return 0;
      const typeId = childStructure.structureTypeId;
      if (typeId === 35825) return 0.03;
      if (typeId === 35826) return 0.04;
      if (typeId === 35827) return 0.05;
      if (typeId === 35835) return 0.02;
      if (typeId === 35836) return 0.025;
      return 0;
    };

    const systemCostIndex = isChildReaction
      ? (childStructure?.solarSystem.costIndices?.reaction ?? 0)
      : (childStructure?.solarSystem.costIndices?.manufacturing ?? 0);
    const systemCost = eiv * systemCostIndex;
    const structureRoleBonus = systemCost * getStructureRoleBonus();
    const jobGrossCost = systemCost - structureRoleBonus;
    const sccSurcharge = eiv * 0.04;
    const facilityTax = eiv * (childStructure?.facilityTax ?? 0);
    const totalJobCost = jobGrossCost + facilityTax + sccSurcharge;

    const totalCost = totalMaterialCost + totalJobCost;

    // Return cost per unit of output
    return totalCost / totalOutputQuantity;
  }, [tree, structures, buildDecisions, adjustedPrices, janicePrices, sharedNodes, findNodeById]);

  // Get blueprint icon URL
  const getBlueprintIcon = (blueprint: Blueprint) => {
    const suffix = blueprint.metaLevel === 1 ? 'bp' : 'bpc';
    return `https://images.evetech.net/types/${blueprint.blueprintTypeId}/${suffix}?size=64`;
  };

  // Get blueprint name
  const getBlueprintName = (blueprint: Blueprint) => {
    return blueprint.type?.name?.en || `Blueprint ${blueprint.blueprintTypeId}`;
  };

  // Get max runs for BPO
  const getMaxRunsBPO = (blueprint: Blueprint) => {
    if (blueprint.activity === 'reaction') {
      return blueprint.activities?.reaction?.time ? Math.floor(2592000 / blueprint.activities.reaction.time) : 1;
    }
    return blueprint.activities?.manufacturing?.time ? Math.floor(2592000 / blueprint.activities.manufacturing.time) : 1;
  };

  // Check if current blueprint is a reaction
  const isReaction = selectedNode?.blueprint?.activity === 'reaction';

  // Get settings from selected node
  const nodeSettings = selectedNode?.settings ?? defaultNodeSettings;
  const selectedStructure = structures.find(s => s.id === nodeSettings.structureId) ?? null;

  // Derived values for material calculations
  const getFacilityType = (): 'NPC' | 'Medium' | 'Large' | 'X-Large' => {
    if (!selectedStructure) return 'NPC';
    const typeId = selectedStructure.structureTypeId;
    if (typeId === 35825) return 'Medium'; // Raitaru
    if (typeId === 35826) return 'Large';  // Azbel
    if (typeId === 35827) return 'X-Large'; // Sotiyo
    return 'NPC';
  };

  const getSystemType = (): 'High' | 'Low' | 'Null' => {
    if (!selectedStructure) return 'High';
    const sec = selectedStructure.solarSystem.securityStatus;
    if (sec === null) return 'High';
    if (sec >= 0.5) return 'High';
    if (sec > 0.0) return 'Low';
    return 'Null';
  };

  const getMaterialRig = (): 'None' | 'T1' | 'T2' => {
    if (nodeSettings.materialRigOverride) return nodeSettings.materialRigOverride as 'None' | 'T1' | 'T2';
    if (!selectedStructure) return 'None';
    return (selectedStructure.materialRig || 'None') as 'None' | 'T1' | 'T2';
  };

  const getReactionRig = (): 'None' | 'T1' | 'T2' => {
    if (nodeSettings.reactionRigOverride) return nodeSettings.reactionRigOverride as 'None' | 'T1' | 'T2';
    if (!selectedStructure) return 'None';
    return (selectedStructure.reactionRig || 'None') as 'None' | 'T1' | 'T2';
  };

  const getTimeRig = (): 'None' | 'T1' | 'T2' => {
    if (nodeSettings.timeRigOverride) return nodeSettings.timeRigOverride as 'None' | 'T1' | 'T2';
    if (!selectedStructure) return 'None';
    return (selectedStructure.timeRig || 'None') as 'None' | 'T1' | 'T2';
  };

  const facility = getFacilityType();
  const system = getSystemType();
  const effectiveMaterialRig = isReaction ? getReactionRig() : getMaterialRig();
  const effectiveTimeRig = getTimeRig();

  // Tree line component using SVG for smooth corners
  const TreeConnector = ({ isLast, hasChildren, isExpanded, onToggle }: {
    isLast: boolean;
    hasChildren: boolean;
    isExpanded: boolean;
    onToggle: () => void;
  }) => (
    <div className="relative flex-shrink-0" style={{ width: '20px', height: '40px' }}>
      <svg
        className="absolute inset-0"
        width="20"
        height="40"
      >
        {/* Vertical line - from top to middle */}
        <line x1="8" y1="0" x2="8" y2="20" stroke="#4B5563" strokeWidth="1" />
        {/* Vertical line - from middle to bottom (if not last) */}
        {!isLast && <line x1="8" y1="20" x2="8" y2="40" stroke="#4B5563" strokeWidth="1" />}
        {/* Horizontal line from vertical to node */}
        <line x1="8" y1="20" x2="20" y2="20" stroke="#4B5563" strokeWidth="1" />
      </svg>
      {/* Expand/collapse button - positioned on top of the junction */}
      {hasChildren && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          className="absolute z-10 w-3 h-3 flex items-center justify-center text-[10px] leading-none text-gray-400 hover:text-white bg-gray-800 border border-gray-600 rounded-sm"
          style={{ left: '2px', top: '50%', transform: 'translateY(-50%)' }}
        >
          {isExpanded ? '−' : '+'}
        </button>
      )}
    </div>
  );

  // Render tree node with proper connecting lines
  const renderTreeNode = (
    node: TreeNode,
    depth: number = 0,
    isLastChild: boolean = false,
    ancestorIsLast: boolean[] = []
  ): React.ReactNode => {
    const isRoot = node.id === 'root';
    const isSelected = selectedNodeId === node.id;
    const hasChildren = node.children.length > 0;
    const isGhost = node.isGhostRef === true;
    const isLevel1 = depth === 1; // Direct children of root

    const handleRemoveNode = (e: React.MouseEvent) => {
      e.stopPropagation(); // Prevent node selection

      // Helper to find all shared (defining) nodes under a tree branch
      const findSharedNodesUnder = (n: TreeNode): TreeNode[] => {
        const result: TreeNode[] = [];
        if (n.id.startsWith('shared-') && !n.isGhostRef) {
          result.push(n);
        }
        for (const child of n.children) {
          result.push(...findSharedNodesUnder(child));
        }
        return result;
      };

      // Helper to find all ghost nodes referencing a defining node, across all level 1 items except the one being removed
      const findGhostRefsForDefiningNode = (definingNodeId: string, excludeLevel1NodeId: string): { ghostNode: TreeNode; parentNode: TreeNode; level1Node: TreeNode }[] => {
        const results: { ghostNode: TreeNode; parentNode: TreeNode; level1Node: TreeNode }[] = [];

        const searchInNode = (n: TreeNode, level1: TreeNode, parent: TreeNode | null) => {
          if (n.isGhostRef && n.definingNodeId === definingNodeId) {
            results.push({ ghostNode: n, parentNode: parent!, level1Node: level1 });
          }
          for (const child of n.children) {
            searchInNode(child, level1, n);
          }
        };

        // Search in all level 1 nodes except the one being removed
        for (const level1Node of tree.children) {
          if (level1Node.id !== excludeLevel1NodeId) {
            searchInNode(level1Node, level1Node, null);
          }
        }
        return results;
      };

      // Find all shared nodes that will be orphaned by removing this level 1 item
      const sharedNodesUnderRemoved = findSharedNodesUnder(node);

      // Process each shared node - transfer ownership if there are ghost references elsewhere
      const sharedNodesToRemove: number[] = [];
      const sharedNodeUpdates: Record<number, TreeNode> = {};

      // Build updated tree with ownership transfers
      let updatedTree = { ...tree, children: tree.children.filter(child => child.id !== node.id) };

      for (const sharedNode of sharedNodesUnderRemoved) {
        const materialTypeId = sharedNode.parentMaterialTypeId;
        if (!materialTypeId) continue;

        const ghostRefs = findGhostRefsForDefiningNode(sharedNode.id, node.id);

        if (ghostRefs.length > 0) {
          // There are ghost references under other level 1 items - promote the first one
          const { ghostNode, parentNode } = ghostRefs[0];

          // Create new defining node from the ghost's data
          const newDefiningNodeId = `shared-${materialTypeId}`;
          const newDefiningNode: TreeNode = {
            ...sharedNode, // Copy all data from original shared node
            id: newDefiningNodeId,
            parentNodeId: parentNode.id,
            parentMaterialTypeId: materialTypeId,
            isGhostRef: undefined,
            definingNodeId: undefined,
            parentRefs: ghostRefs.map(ref => ({ nodeId: ref.parentNode.id, materialTypeId })),
          };

          // Update the tree: replace the ghost with the new defining node, update other ghosts
          const replaceGhostWithDefining = (n: TreeNode): TreeNode => {
            if (n.id === ghostNode.id) {
              // Replace this ghost with the new defining node
              return newDefiningNode;
            }
            // Update other ghosts to point to new defining node
            if (n.isGhostRef && n.definingNodeId === sharedNode.id) {
              return { ...n, definingNodeId: newDefiningNodeId };
            }
            return { ...n, children: n.children.map(replaceGhostWithDefining) };
          };

          updatedTree = replaceGhostWithDefining(updatedTree);
          sharedNodeUpdates[materialTypeId] = newDefiningNode;
        } else {
          // No ghost references - this shared node can be fully removed
          sharedNodesToRemove.push(materialTypeId);
        }
      }

      // Apply tree update
      setTree(updatedTree);

      // Update sharedNodes registry
      setSharedNodes(prev => {
        const updated = { ...prev, ...sharedNodeUpdates };
        for (const typeId of sharedNodesToRemove) {
          delete updated[typeId];
        }
        return updated;
      });

      // If this node was selected, select root instead
      if (selectedNodeId === node.id) {
        setSelectedNodeId('root');
      }
    };

    const toggleExpand = () => {
      if (isGhost) return; // Ghost nodes don't expand
      setTree(prevTree => {
        const toggle = (n: TreeNode): TreeNode => {
          if (n.id === node.id) {
            return { ...n, expanded: !n.expanded };
          }
          return { ...n, children: n.children.map(toggle) };
        };
        return toggle(prevTree);
      });
    };

    const handleNodeClick = () => {
      if (isGhost && node.definingNodeId) {
        // Ghost node clicked - jump to the defining node
        setSelectedNodeId(node.definingNodeId);
      } else {
        setSelectedNodeId(node.id);
      }
    };

    return (
      <div key={node.id}>
        {/* Node row */}
        <div
          className={`group flex items-center gap-1 cursor-pointer transition-colors rounded ${
            isSelected ? 'bg-blue-600' : 'hover:bg-gray-700'
          } ${isGhost ? 'opacity-50' : ''}`}
          style={{ paddingLeft: isRoot ? '8px' : '0px', paddingRight: '8px' }}
          onClick={handleNodeClick}
          title={isGhost ? 'Click to jump to defining node' : undefined}
        >
          {/* Ancestor vertical lines */}
          {!isRoot && ancestorIsLast.map((isAncestorLast, idx) => (
            <div key={idx} className="relative flex-shrink-0" style={{ width: '20px', height: '40px' }}>
              {!isAncestorLast && (
                <svg width="20" height="40" className="absolute inset-0">
                  <line x1="8" y1="0" x2="8" y2="40" stroke="#4B5563" strokeWidth="1" />
                </svg>
              )}
            </div>
          ))}

          {/* Tree connector for this node */}
          {!isRoot && (
            <TreeConnector
              isLast={isLastChild}
              hasChildren={isGhost ? false : hasChildren}
              isExpanded={isGhost ? false : node.expanded}
              onToggle={toggleExpand}
            />
          )}

          {/* Icon */}
          {isRoot ? (
            <div className="w-8 h-8 bg-gray-700 rounded flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
          ) : node.blueprint ? (
            <img
              src={getBlueprintIcon(node.blueprint)}
              alt={getBlueprintName(node.blueprint)}
              className="w-8 h-8 rounded flex-shrink-0"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          ) : null}

          {/* Name */}
          <span className={`text-sm truncate ml-1 ${isSelected ? 'text-white font-medium' : 'text-gray-300'}`}>
            {isRoot ? 'Summary' : node.blueprint ? getBlueprintName(node.blueprint) : 'Unknown'}
          </span>

          {/* Shared node indicator - only show if this typeId appears more than once */}
          {!isRoot && node.parentMaterialTypeId && sharedNodes[node.parentMaterialTypeId]?.parentRefs && sharedNodes[node.parentMaterialTypeId].parentRefs!.length > 1 && (
            <span className="flex-shrink-0 text-yellow-500" title={`Shared node - changes affect ${sharedNodes[node.parentMaterialTypeId].parentRefs!.length} locations`}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
            </span>
          )}

          {/* Reaction badge */}
          {!isRoot && node.blueprint?.activity === 'reaction' && (
            <span className="px-1 py-0.5 text-[10px] bg-purple-600 text-white rounded flex-shrink-0">R</span>
          )}

          {/* Remove button - only for level 1 items, visible on hover */}
          {isLevel1 && (
            <button
              onClick={handleRemoveNode}
              className="ml-auto flex-shrink-0 p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-red-600 hover:text-white text-gray-400 transition-all"
              title="Remove from tree"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Children */}
        {node.expanded && (() => {
          // Filter out ghost nodes if the filter is enabled
          const visibleChildren = filterSharedNodes
            ? node.children.filter(child => !child.isGhostRef)
            : node.children;

          return visibleChildren.map((child, index) => {
            // For root's direct children, none are "last" because the Add button is always last
            const childIsLast = isRoot ? false : index === visibleChildren.length - 1;
            return renderTreeNode(
              child,
              depth + 1,
              childIsLast,
              isRoot ? [] : [...ancestorIsLast, isLastChild]
            );
          });
        })()}

        {/* Add button - only show for root node, after all children */}
        {isRoot && node.expanded && (
          <div
            className="flex items-center gap-1 cursor-pointer hover:bg-gray-700/50 rounded transition-colors"
            style={{ paddingRight: '8px' }}
            onClick={() => handleAddClick(node.id)}
          >
            {/* Tree connector for add button (it's always the last item) */}
            <div className="relative flex-shrink-0" style={{ width: '20px', height: '32px' }}>
              <svg width="20" height="32" className="absolute inset-0">
                <line x1="8" y1="0" x2="8" y2="16" stroke="#4B5563" strokeWidth="1" />
                <line x1="8" y1="16" x2="20" y2="16" stroke="#4B5563" strokeWidth="1" />
              </svg>
            </div>
            <div className="w-6 h-6 bg-gray-700 hover:bg-green-600 rounded flex items-center justify-center transition-colors flex-shrink-0">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </div>
            <span className="text-xs text-gray-500 ml-1">Add blueprint</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <Layout>
      <div className="h-full flex">
        {/* Left Panel - Tree Navigation (20%) */}
        <div className="w-1/5 border-r border-gray-700 flex flex-col bg-gray-900">
          <div className="p-4 border-b border-gray-700">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">Build Tree</h2>
              <label className="flex items-center gap-2 cursor-pointer">
                <span className="text-xs text-gray-400">Filter shared</span>
                <div
                  className={`relative w-8 h-4 rounded-full transition-colors ${filterSharedNodes ? 'bg-blue-600' : 'bg-gray-600'}`}
                  onClick={() => setFilterSharedNodes(!filterSharedNodes)}
                >
                  <div
                    className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-transform ${filterSharedNodes ? 'translate-x-4' : 'translate-x-0.5'}`}
                  />
                </div>
              </label>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-2">
            {renderTreeNode(tree)}
          </div>
        </div>

        {/* Right Panel - Details (80%) */}
        <div className="flex-1 bg-gray-800 overflow-auto">
          {selectedNodeId === 'root' ? (
            // Summary view
            (() => {
              // If tree has no children, show empty state
              if (tree.children.length === 0) {
                return (
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center text-gray-500">
                      <svg className="w-16 h-16 mx-auto mb-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      <p className="text-lg">Summary</p>
                      <p className="text-sm mt-2">Add blueprints to see the build summary</p>
                    </div>
                  </div>
                );
              }

              // Types for summary calculation
              interface ShoppingItem {
                typeId: number;
                name: string;
                iconId: number | null;
                quantity: number;
                volume: number;
                price: number | null;
                marketGroupId: number | null;
                marketGroupName: string | null;
              }

              interface IntermediateItem {
                typeId: number;
                name: string;
                iconId: number | null;
                quantity: number; // Total quantity needed
                runs: number; // Number of runs required
                outputPerRun: number;
                activity: 'manufacturing' | 'reaction';
                nodeId: string;
                marketGroupId: number | null;
                marketGroupName: string | null;
              }

              // Maps to accumulate quantities
              const shoppingList: Map<number, ShoppingItem> = new Map();
              const intermediateItems: Map<number, IntermediateItem> = new Map();

              // Helper to get structure details for a node
              const getNodeStructure = (node: TreeNode) => {
                return structures.find(s => s.id === node.settings.structureId) ?? null;
              };

              // Helper to get facility type
              const getNodeFacilityType = (structure: Structure | null): 'NPC' | 'Medium' | 'Large' | 'X-Large' => {
                if (!structure) return 'NPC';
                const typeId = structure.structureTypeId;
                if (typeId === 35825) return 'Medium';
                if (typeId === 35826) return 'Large';
                if (typeId === 35827) return 'X-Large';
                return 'NPC';
              };

              // Helper to get system type
              const getNodeSystemType = (structure: Structure | null): 'High' | 'Low' | 'Null' => {
                if (!structure) return 'High';
                const sec = structure.solarSystem.securityStatus;
                if (sec === null) return 'High';
                if (sec >= 0.5) return 'High';
                if (sec > 0) return 'Low';
                return 'Null';
              };

              // Helper to get effective material rig
              const getNodeMaterialRig = (node: TreeNode, structure: Structure | null, isReaction: boolean): 'None' | 'T1' | 'T2' => {
                if (!structure) return 'None';
                if (isReaction) {
                  return (node.settings.reactionRigOverride ?? structure.reactionRig) as 'None' | 'T1' | 'T2';
                }
                return (node.settings.materialRigOverride ?? structure.materialRig) as 'None' | 'T1' | 'T2';
              };

              // Track demand for each node (by node ID) - we'll accumulate demand first
              const nodeDemands: Map<string, number> = new Map();

              // First pass: collect all demands for each node
              const collectDemands = (node: TreeNode, demandQuantity: number) => {
                if (!node.blueprintDetails || !node.blueprint) return;

                const isNodeReaction = node.blueprint.activity === 'reaction';
                const activityType = isNodeReaction ? 'reaction' : 'manufacturing';
                const materials = node.blueprintDetails.materials.filter(m => m.activity === activityType);
                const products = node.blueprintDetails.products.filter(p => p.activity === activityType);
                const outputProduct = products[0];
                const outputPerRun = outputProduct?.quantity ?? 1;

                // Accumulate demand for this node
                const currentDemand = nodeDemands.get(node.id) ?? 0;
                nodeDemands.set(node.id, currentDemand + demandQuantity);

                // Calculate runs needed for current accumulated demand
                const totalDemand = currentDemand + demandQuantity;
                const requiredRuns = Math.max(1, Math.ceil(totalDemand / outputPerRun));
                const actualRuns = Math.max(node.settings.runs, requiredRuns);

                // Get structure and calculation parameters
                const structure = getNodeStructure(node);
                const facility = getNodeFacilityType(structure);
                const system = getNodeSystemType(structure);
                const effectiveRig = getNodeMaterialRig(node, structure, isNodeReaction);

                // Process each material to propagate demand to children
                for (const material of materials) {
                  // Calculate adjusted quantity with ME and rigs based on required runs
                  const adjustedQuantity = calculateMaterialQuantity(
                    material.quantity,
                    node.settings.materialEfficiency,
                    actualRuns,
                    facility,
                    effectiveRig,
                    system,
                    isNodeReaction
                  );

                  // Check if this material is being built (has a child node)
                  const materialDecision = buildDecisions[node.id]?.[material.typeId];

                  if (materialDecision?.mode === 'build' && materialDecision.childNodeId) {
                    // Find the child node
                    let childNode: TreeNode | null = null;

                    if (materialDecision.childNodeId.startsWith('ghost-')) {
                      const typeIdMatch = materialDecision.childNodeId.match(/ghost-(\d+)-/);
                      if (typeIdMatch) {
                        const typeId = parseInt(typeIdMatch[1]);
                        childNode = sharedNodes[typeId] ?? null;
                      }
                    } else if (materialDecision.childNodeId.startsWith('shared-')) {
                      const typeId = parseInt(materialDecision.childNodeId.replace('shared-', ''));
                      childNode = sharedNodes[typeId] ?? null;
                    } else {
                      const findChild = (n: TreeNode): TreeNode | null => {
                        if (n.id === materialDecision.childNodeId) return n;
                        for (const c of n.children) {
                          const found = findChild(c);
                          if (found) return found;
                        }
                        return null;
                      };
                      childNode = findChild(tree);
                    }

                    if (childNode && !childNode.isGhostRef) {
                      collectDemands(childNode, adjustedQuantity);
                    }
                  }
                }
              };

              // Collect demands for all level 1 nodes
              for (const level1Node of tree.children) {
                if (level1Node.blueprintDetails) {
                  const activityType = level1Node.blueprint?.activity === 'reaction' ? 'reaction' : 'manufacturing';
                  const products = level1Node.blueprintDetails.products.filter(p => p.activity === activityType);
                  const outputQuantity = products[0]?.quantity ?? 1;
                  const totalOutput = level1Node.settings.runs * outputQuantity;
                  collectDemands(level1Node, totalOutput);
                }
              }

              // Second pass: calculate actual runs and material quantities based on accumulated demands
              const processNode = (node: TreeNode) => {
                if (!node.blueprintDetails || !node.blueprint) return;

                const isNodeReaction = node.blueprint.activity === 'reaction';
                const activityType = isNodeReaction ? 'reaction' : 'manufacturing';
                const materials = node.blueprintDetails.materials.filter(m => m.activity === activityType);
                const products = node.blueprintDetails.products.filter(p => p.activity === activityType);
                const outputProduct = products[0];
                const outputPerRun = outputProduct?.quantity ?? 1;

                // Get total demand for this node
                const totalDemand = nodeDemands.get(node.id) ?? 0;
                if (totalDemand === 0) return;

                // Calculate required runs based on total demand (always round up)
                const requiredRuns = Math.max(1, Math.ceil(totalDemand / outputPerRun));
                const actualRuns = Math.max(node.settings.runs, requiredRuns);

                // Get structure and calculation parameters
                const structure = getNodeStructure(node);
                const facility = getNodeFacilityType(structure);
                const system = getNodeSystemType(structure);
                const effectiveRig = getNodeMaterialRig(node, structure, isNodeReaction);

                // Add to intermediate items
                if (outputProduct) {
                  intermediateItems.set(outputProduct.typeId, {
                    typeId: outputProduct.typeId,
                    name: outputProduct.type.name?.en || `Type ${outputProduct.typeId}`,
                    iconId: outputProduct.type.iconId,
                    quantity: totalDemand,
                    runs: actualRuns,
                    outputPerRun,
                    activity: isNodeReaction ? 'reaction' : 'manufacturing',
                    nodeId: node.id,
                    marketGroupId: (outputProduct.type as any).marketGroupId ?? null,
                    marketGroupName: (outputProduct.type as any).marketGroup?.nameEn ?? null,
                  });
                }

                // Process each material
                for (const material of materials) {
                  // Calculate adjusted quantity with ME and rigs based on ACTUAL runs (rounded up)
                  const adjustedQuantity = calculateMaterialQuantity(
                    material.quantity,
                    node.settings.materialEfficiency,
                    actualRuns,
                    facility,
                    effectiveRig,
                    system,
                    isNodeReaction
                  );

                  // Check if this material is being built (has a child node)
                  const materialDecision = buildDecisions[node.id]?.[material.typeId];

                  if (materialDecision?.mode === 'build' && materialDecision.childNodeId) {
                    // Material is being built - skip adding to shopping list
                    // (the child node will be processed separately)
                  } else {
                    // This material is bought - add to shopping list
                    const existingItem = shoppingList.get(material.typeId);
                    const janicePrice = janicePrices[material.typeId];
                    const pricePerUnit = janicePrice?.buy ?? null;

                    if (existingItem) {
                      existingItem.quantity += adjustedQuantity;
                      if (pricePerUnit !== null && existingItem.price !== null) {
                        existingItem.price = pricePerUnit * existingItem.quantity;
                      }
                    } else {
                      shoppingList.set(material.typeId, {
                        typeId: material.typeId,
                        name: material.type.name?.en || `Type ${material.typeId}`,
                        iconId: material.type.iconId,
                        quantity: adjustedQuantity,
                        volume: material.type.volume ?? 0,
                        price: pricePerUnit !== null ? pricePerUnit * adjustedQuantity : null,
                        marketGroupId: (material.type as any).marketGroupId ?? null,
                        marketGroupName: (material.type as any).marketGroup?.nameEn ?? null,
                      });
                    }
                  }
                }
              };

              // Helper to find and process all nodes in tree that have demand
              const processAllNodes = (node: TreeNode) => {
                if (nodeDemands.has(node.id)) {
                  processNode(node);
                }
                for (const child of node.children) {
                  if (!child.isGhostRef) {
                    processAllNodes(child);
                  }
                }
              };

              // Process all nodes in tree
              processAllNodes(tree);

              // Also process shared nodes (they may not be in the main tree traversal)
              for (const sharedNode of Object.values(sharedNodes)) {
                if (nodeDemands.has(sharedNode.id) && !intermediateItems.has(sharedNode.parentMaterialTypeId ?? 0)) {
                  processNode(sharedNode);
                }
              }

              // Convert maps to arrays and sort by market group, then by name
              const shoppingItems = Array.from(shoppingList.values()).sort((a, b) => {
                // First sort by market group name (nulls last)
                const groupA = a.marketGroupName ?? 'zzz';
                const groupB = b.marketGroupName ?? 'zzz';
                if (groupA !== groupB) return groupA.localeCompare(groupB);
                // Then by item name
                return a.name.localeCompare(b.name);
              });
              const intermediates = Array.from(intermediateItems.values()).sort((a, b) => {
                // First sort by market group name (nulls last)
                const groupA = a.marketGroupName ?? 'zzz';
                const groupB = b.marketGroupName ?? 'zzz';
                if (groupA !== groupB) return groupA.localeCompare(groupB);
                // Then by item name
                return a.name.localeCompare(b.name);
              });

              // Group items by market group for display
              const groupShoppingByMarket = () => {
                const groups: Map<string, ShoppingItem[]> = new Map();
                for (const item of shoppingItems) {
                  const groupName = item.marketGroupName ?? 'Unknown';
                  if (!groups.has(groupName)) {
                    groups.set(groupName, []);
                  }
                  groups.get(groupName)!.push(item);
                }
                return Array.from(groups.entries()).sort((a, b) => a[0].localeCompare(b[0]));
              };

              const groupIntermediatesByMarket = () => {
                const groups: Map<string, IntermediateItem[]> = new Map();
                for (const item of intermediates) {
                  const groupName = item.marketGroupName ?? 'Unknown';
                  if (!groups.has(groupName)) {
                    groups.set(groupName, []);
                  }
                  groups.get(groupName)!.push(item);
                }
                return Array.from(groups.entries()).sort((a, b) => a[0].localeCompare(b[0]));
              };

              const shoppingByGroup = groupShoppingByMarket();
              const intermediatesByGroup = groupIntermediatesByMarket();

              // Calculate totals
              const totalVolume = shoppingItems.reduce((sum, item) => sum + (item.volume * item.quantity), 0);
              const totalPrice = shoppingItems.reduce((sum, item) => sum + (item.price ?? 0), 0);

              return (
                <div className="h-full flex flex-col">
                  {/* Header */}
                  <div className="p-6 border-b border-gray-700 bg-gray-900">
                    <h1 className="text-2xl font-bold text-white">Build Summary</h1>
                    <p className="text-sm text-gray-400 mt-1">
                      {tree.children.length} blueprint{tree.children.length !== 1 ? 's' : ''} in tree
                    </p>
                  </div>

                  {/* Content */}
                  <div className="flex-1 p-6 overflow-hidden flex flex-col min-h-0">
                    <div className="grid grid-cols-2 gap-6 flex-1 min-h-0">
                      {/* Shopping List */}
                      <div className="bg-gray-900 rounded-lg border border-gray-700 flex flex-col min-h-0">
                        <div className="p-4 border-b border-gray-700">
                          <div className="flex items-center justify-between">
                            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                              <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                              </svg>
                              Shopping List ({shoppingItems.length})
                            </h2>
                            {shoppingItems.length > 0 && (
                              <button
                                onClick={() => {
                                  const text = shoppingItems
                                    .map(item => `${item.name} ${item.quantity}`)
                                    .join('\n');
                                  navigator.clipboard.writeText(text);
                                }}
                                className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 rounded transition-colors"
                                title="Copy for EVE Multibuy"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                                Copy for Multibuy
                              </button>
                            )}
                          </div>
                          <div className="flex gap-4 mt-2 text-sm">
                            <span className="text-gray-400">
                              Volume: <span className="text-white font-mono">{totalVolume.toLocaleString()} m³</span>
                            </span>
                            <span className="text-gray-400">
                              Est. Cost: <span className="text-green-400 font-mono">{totalPrice.toLocaleString(undefined, { maximumFractionDigits: 0 })} ISK</span>
                            </span>
                          </div>
                        </div>
                        <div className="flex-1 overflow-y-auto min-h-0">
                          <table className="w-full">
                            <thead className="bg-gray-800 sticky top-0">
                              <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">Item</th>
                                <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase">Qty</th>
                                <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase">Volume</th>
                                <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase">Price</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-700">
                              {shoppingByGroup.map(([groupName, items]) => (
                                <React.Fragment key={groupName}>
                                  <tr className="bg-gray-800/50">
                                    <td colSpan={4} className="px-4 py-1.5 text-xs font-semibold text-blue-400 uppercase tracking-wider">
                                      {groupName} ({items.length})
                                    </td>
                                  </tr>
                                  {items.map(item => (
                                    <tr key={item.typeId} className="hover:bg-gray-800">
                                      <td className="px-4 py-2">
                                        <div className="flex items-center gap-2">
                                          <img
                                            src={`https://images.evetech.net/types/${item.typeId}/icon?size=32`}
                                            alt={item.name}
                                            className="w-6 h-6 rounded"
                                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                          />
                                          <span className="text-sm text-white">{item.name}</span>
                                        </div>
                                      </td>
                                      <td className="px-4 py-2 text-right text-sm font-mono text-white">
                                        {item.quantity.toLocaleString()}
                                      </td>
                                      <td className="px-4 py-2 text-right text-sm text-gray-400">
                                        {(item.volume * item.quantity).toLocaleString()} m³
                                      </td>
                                      <td className="px-4 py-2 text-right text-sm font-mono text-green-400">
                                        {item.price !== null ? item.price.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '-'}
                                      </td>
                                    </tr>
                                  ))}
                                </React.Fragment>
                              ))}
                              {shoppingItems.length === 0 && (
                                <tr>
                                  <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                                    No items to buy - all materials are being built
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Intermediate Items */}
                      <div className="bg-gray-900 rounded-lg border border-gray-700 flex flex-col min-h-0">
                        <div className="p-4 border-b border-gray-700">
                          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                            <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                            </svg>
                            Intermediate Items ({intermediates.length})
                          </h2>
                        </div>
                        <div className="flex-1 overflow-y-auto min-h-0">
                          <table className="w-full">
                            <thead className="bg-gray-800 sticky top-0">
                              <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">Item</th>
                                <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase">Qty Needed</th>
                                <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase">Runs</th>
                                <th className="px-4 py-2 text-center text-xs font-medium text-gray-400 uppercase">Type</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-700">
                              {intermediatesByGroup.map(([groupName, items]) => (
                                <React.Fragment key={groupName}>
                                  <tr className="bg-gray-800/50">
                                    <td colSpan={4} className="px-4 py-1.5 text-xs font-semibold text-purple-400 uppercase tracking-wider">
                                      {groupName} ({items.length})
                                    </td>
                                  </tr>
                                  {items.map(item => (
                                    <tr
                                      key={item.typeId}
                                      className="hover:bg-gray-800 cursor-pointer"
                                      onClick={() => setSelectedNodeId(item.nodeId)}
                                    >
                                      <td className="px-4 py-2">
                                        <div className="flex items-center gap-2">
                                          <img
                                            src={`https://images.evetech.net/types/${item.typeId}/icon?size=32`}
                                            alt={item.name}
                                            className="w-6 h-6 rounded"
                                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                          />
                                          <span className="text-sm text-white">{item.name}</span>
                                        </div>
                                      </td>
                                      <td className="px-4 py-2 text-right text-sm font-mono text-white">
                                        {item.quantity.toLocaleString()}
                                      </td>
                                      <td className="px-4 py-2 text-right text-sm font-mono text-yellow-400">
                                        {item.runs.toLocaleString()}
                                      </td>
                                      <td className="px-4 py-2 text-center">
                                        <span className={`px-2 py-0.5 text-xs rounded ${
                                          item.activity === 'reaction'
                                            ? 'bg-purple-600 text-white'
                                            : 'bg-blue-600 text-white'
                                        }`}>
                                          {item.activity === 'reaction' ? 'Reaction' : 'Manufacturing'}
                                        </span>
                                      </td>
                                    </tr>
                                  ))}
                                </React.Fragment>
                              ))}
                              {intermediates.length === 0 && (
                                <tr>
                                  <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                                    No intermediate items - nothing is being built
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()
          ) : selectedNode?.blueprint ? (
            // Blueprint details view
            <div className="h-full flex flex-col">
              {/* Header */}
              <div className="p-6 border-b border-gray-700 bg-gray-900">
                <div className="flex items-start gap-6">
                  {/* Left: Blueprint Info */}
                  <div className="flex items-center space-x-4">
                    <img
                      src={getBlueprintIcon(selectedNode.blueprint)}
                      alt={getBlueprintName(selectedNode.blueprint)}
                      className="w-16 h-16 rounded-lg"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                    <div>
                      <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                        {getBlueprintName(selectedNode.blueprint)}
                        <span className="text-sm text-gray-400">({selectedNode.blueprint.blueprintTypeId})</span>
                        {isReaction && (
                          <span className="px-2 py-0.5 text-xs bg-purple-600 text-white rounded">Reaction</span>
                        )}
                      </h1>
                      <p className="text-sm text-gray-400">
                        {isReaction
                          ? 'Reaction Formula'
                          : `Max Runs BPO: ${getMaxRunsBPO(selectedNode.blueprint).toLocaleString()} | Max Runs BPC: ${selectedNode.blueprint.maxProductionLimit.toLocaleString()}`
                        }
                      </p>
                    </div>
                  </div>

                  {/* Right: Controls */}
                  <div className="flex flex-col gap-3 ml-auto items-end">
                    {/* First Row: Runs and Janice Price Controls */}
                    <div className="flex items-center gap-4 justify-end">
                      {/* Runs */}
                      <div className="flex items-center gap-2">
                        <label className="text-sm text-gray-400 font-medium">Runs:</label>
                        <input
                          type="number"
                          min="1"
                          max={getMaxRunsBPO(selectedNode.blueprint)}
                          value={nodeSettings.runs}
                          onChange={(e) => {
                            const value = parseInt(e.target.value);
                            if (!isNaN(value) && value >= 1) {
                              updateNodeSettings(selectedNodeId, { runs: value });
                            }
                          }}
                          className="w-20 px-2 py-1 bg-gray-800 border border-gray-700 rounded-lg text-white text-center font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      {/* Janice Price Type Selection */}
                      <div className="flex items-center gap-2">
                        <label className="text-sm text-gray-400 font-medium">Janice Price:</label>
                        <div className="inline-flex rounded-lg border border-gray-700 overflow-hidden">
                          {(['buy', 'split', 'sell'] as const).map((type) => (
                            <button
                              key={type}
                              onClick={() => updateNodeSettings(selectedNodeId, { janicePriceType: type })}
                              className={`px-3 py-1 text-xs font-medium transition-colors ${
                                type !== 'buy' ? 'border-l border-gray-700' : ''
                              } ${
                                nodeSettings.janicePriceType === type
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                              }`}
                            >
                              {type.charAt(0).toUpperCase() + type.slice(1)}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Second Row: Structure Selector */}
                    <div className="flex items-center gap-4 justify-end">
                      <div className="flex items-center gap-2">
                        <label className="text-sm text-gray-400 font-medium">Structure:</label>
                        <select
                          value={nodeSettings.structureId || ''}
                          onChange={(e) => {
                            updateNodeSettings(selectedNodeId, { structureId: e.target.value || null });
                          }}
                          className="px-3 py-1 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">No Structure (NPC Station)</option>
                          {structures.map((structure) => (
                            <option key={structure.id} value={structure.id}>
                              {structure.solarSystem.nameEn} - {structure.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Structure info */}
                      {selectedStructure && (
                        <div className="flex items-center gap-3 text-xs text-gray-400">
                          <span>
                            Sec: <span className={`font-medium ${
                              (selectedStructure.solarSystem.securityStatus ?? 0) >= 0.5 ? 'text-green-400' :
                              (selectedStructure.solarSystem.securityStatus ?? 0) > 0 ? 'text-yellow-400' : 'text-red-400'
                            }`}>
                              {(selectedStructure.solarSystem.securityStatus ?? 0).toFixed(2)}
                            </span>
                          </span>
                          {selectedStructure.solarSystem.costIndices?.manufacturing !== undefined && (
                            <span>
                              Cost Index: <span className="text-white">
                                {(selectedStructure.solarSystem.costIndices.manufacturing * 100).toFixed(2)}%
                              </span>
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Third Row: ME/TE and Rig Controls */}
                    <div className="flex items-center gap-4 justify-end flex-wrap">
                      {/* ME Slider (not for reactions) */}
                      {!isReaction && (
                        <div className="flex items-center gap-2">
                          <label className="text-sm text-gray-400 font-medium">ME:</label>
                          <input
                            type="range"
                            min="0"
                            max="10"
                            value={nodeSettings.materialEfficiency}
                            onChange={(e) => updateNodeSettings(selectedNodeId, { materialEfficiency: parseInt(e.target.value) })}
                            className="w-20 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                          />
                          <span className="text-sm font-mono text-white w-6 text-center">{nodeSettings.materialEfficiency}</span>
                        </div>
                      )}

                      {/* TE Slider */}
                      <div className="flex items-center gap-2">
                        <label className="text-sm text-gray-400 font-medium">TE:</label>
                        <input
                          type="range"
                          min="0"
                          max="20"
                          step="2"
                          value={nodeSettings.timeEfficiency}
                          onChange={(e) => updateNodeSettings(selectedNodeId, { timeEfficiency: parseInt(e.target.value) })}
                          className="w-20 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                        />
                        <span className="text-sm font-mono text-white w-6 text-center">{nodeSettings.timeEfficiency}</span>
                      </div>

                      {/* Material/Reaction Rig */}
                      {selectedStructure && (
                        <div className="flex items-center gap-2">
                          <label className="text-xs text-gray-400 font-medium">
                            {isReaction ? 'Reaction Rig:' : 'Material Rig:'}
                          </label>
                          <div className="flex gap-1">
                            {['None', 'T1', 'T2'].map((rig) => {
                              const currentValue = isReaction ? nodeSettings.reactionRigOverride : nodeSettings.materialRigOverride;
                              const structureValue = isReaction ? selectedStructure.reactionRig : selectedStructure.materialRig;
                              const effectiveValue = currentValue ?? structureValue;
                              const isActive = effectiveValue === rig;

                              return (
                                <button
                                  key={rig}
                                  onClick={() => {
                                    if (isReaction) {
                                      updateNodeSettings(selectedNodeId, { reactionRigOverride: rig === structureValue ? null : rig });
                                    } else {
                                      updateNodeSettings(selectedNodeId, { materialRigOverride: rig === structureValue ? null : rig });
                                    }
                                  }}
                                  className={`px-2 py-0.5 text-xs font-medium rounded transition-colors ${
                                    isActive
                                      ? 'bg-blue-600 text-white'
                                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                  }`}
                                >
                                  {rig}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Time Rig */}
                      {selectedStructure && (
                        <div className="flex items-center gap-2">
                          <label className="text-xs text-gray-400 font-medium">Time Rig:</label>
                          <div className="flex gap-1">
                            {['None', 'T1', 'T2'].map((rig) => {
                              const currentValue = nodeSettings.timeRigOverride;
                              const structureValue = selectedStructure.timeRig;
                              const effectiveValue = currentValue ?? structureValue;
                              const isActive = effectiveValue === rig;

                              return (
                                <button
                                  key={rig}
                                  onClick={() => {
                                    updateNodeSettings(selectedNodeId, { timeRigOverride: rig === structureValue ? null : rig });
                                  }}
                                  className={`px-2 py-0.5 text-xs font-medium rounded transition-colors ${
                                    isActive
                                      ? 'bg-blue-600 text-white'
                                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                  }`}
                                >
                                  {rig}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Content area - Materials and Job Costs */}
              <div className="flex-1 p-6 overflow-auto">
                {selectedNode.blueprintDetails && (() => {
                  const details = selectedNode.blueprintDetails;
                  const activityType = isReaction ? 'reaction' : 'manufacturing';
                  const materials = details.materials.filter(m => m.activity === activityType);
                  const products = details.products.filter(p => p.activity === activityType);

                  // Calculate material quantities and costs (accounting for build decisions)
                  const calculateMaterialCosts = () => {
                    let totalVolume = 0;
                    let totalPrice = 0;

                    materials.forEach(m => {
                      const qty = calculateMaterialQuantity(
                        m.quantity,
                        nodeSettings.materialEfficiency,
                        nodeSettings.runs,
                        facility,
                        effectiveMaterialRig,
                        system,
                        isReaction
                      );

                      // Check if this material is being built
                      const materialDecision = buildDecisions[selectedNodeId]?.[m.typeId];
                      let materialPrice = 0;

                      if (materialDecision?.mode === 'build' && materialDecision.childNodeId) {
                        // Use child node cost per unit, then multiply by quantity needed
                        const childCostPerUnit = calculateChildNodeCostPerUnit(materialDecision.childNodeId);
                        materialPrice = (childCostPerUnit ?? 0) * qty;
                      } else {
                        // Use Janice price
                        const price = getJanicePrice(m.typeId, nodeSettings.janicePriceType);
                        materialPrice = (price || 0) * qty;
                      }

                      totalVolume += (m.type.volume || 0) * qty;
                      totalPrice += materialPrice;
                    });

                    return { totalVolume, totalPrice };
                  };

                  const { totalVolume, totalPrice } = calculateMaterialCosts();

                  // Calculate EIV and job costs
                  const eiv = materials.reduce((sum, m) =>
                    sum + (adjustedPrices[m.typeId] ? adjustedPrices[m.typeId] * m.quantity * nodeSettings.runs : 0), 0
                  );

                  const getStructureRoleBonus = (): number => {
                    if (!selectedStructure) return 0;
                    const typeId = selectedStructure.structureTypeId;
                    if (typeId === 35825) return 0.03; // Raitaru
                    if (typeId === 35826) return 0.04; // Azbel
                    if (typeId === 35827) return 0.05; // Sotiyo
                    // Refineries for reactions
                    if (typeId === 35835) return 0.02; // Athanor
                    if (typeId === 35836) return 0.025; // Tatara
                    return 0;
                  };

                  const systemCostIndex = isReaction
                    ? (selectedStructure?.solarSystem.costIndices?.reaction ?? 0)
                    : (selectedStructure?.solarSystem.costIndices?.manufacturing ?? 0);
                  const systemCost = eiv * systemCostIndex;
                  const structureRoleBonus = systemCost * getStructureRoleBonus();
                  const jobGrossCost = systemCost - structureRoleBonus;
                  const sccSurcharge = eiv * 0.04;
                  const facilityTax = eiv * (selectedStructure?.facilityTax ?? 0);
                  const taxes = facilityTax + sccSurcharge;
                  const totalJobCost = jobGrossCost + taxes;
                  const completePrice = totalPrice + totalJobCost;
                  const pricePerRun = completePrice / nodeSettings.runs;

                  // Output product info
                  const outputProduct = products[0];
                  const outputQuantity = outputProduct?.quantity ?? 1;
                  const costPerItem = pricePerRun / outputQuantity;
                  const productPrices = outputProduct ? janicePrices[outputProduct.typeId] : null;

                  // Job duration calculation
                  const activityData = isReaction
                    ? details.activities?.reaction
                    : details.activities?.manufacturing;
                  const baseTimePerRun = activityData?.time || 0;
                  const singleJobTime = calculateProductionTime(
                    baseTimePerRun,
                    nodeSettings.timeEfficiency,
                    nodeSettings.industrySkill,
                    nodeSettings.advancedIndustrySkill,
                    facility,
                    effectiveTimeRig,
                    system
                  );
                  const baseTime = baseTimePerRun * nodeSettings.runs;
                  const actualTime = singleJobTime * nodeSettings.runs;

                  // Calculate optimal runs by simulating from 1 to maxRuns
                  const calculateOptimalRuns = (maxRuns: number) => {
                    let optimalRuns = 1;
                    let lowestCostPerRun = Infinity;

                    for (let testRuns = 1; testRuns <= maxRuns; testRuns++) {
                      // Calculate actual material cost for this run count using Janice prices
                      // Note: For materials set to "build" mode, we still use Janice price for optimization
                      // since we want to find the optimal run count based on consistent pricing
                      const testActualMaterialCost = materials.reduce((sum, m) => {
                        const qty = calculateMaterialQuantity(
                          m.quantity,
                          nodeSettings.materialEfficiency,
                          testRuns,
                          facility,
                          effectiveMaterialRig,
                          system,
                          isReaction
                        );
                        const price = getJanicePrice(m.typeId, nodeSettings.janicePriceType);
                        return sum + (price ? price * qty : 0);
                      }, 0);

                      // Calculate EIV for this run count
                      const testEiv = materials.reduce((sum, m) =>
                        sum + (adjustedPrices[m.typeId] ? adjustedPrices[m.typeId] * m.quantity * testRuns : 0), 0
                      );

                      // Calculate job costs for this run count
                      const testSystemCost = testEiv * systemCostIndex;
                      const testStructureRoleBonus = testSystemCost * getStructureRoleBonus();
                      const testJobGrossCost = testSystemCost - testStructureRoleBonus;
                      const testSccSurcharge = testEiv * 0.04;
                      const testFacilityTax = testEiv * (selectedStructure?.facilityTax ?? 0);
                      const testTaxes = testFacilityTax + testSccSurcharge;
                      const testTotalJobCost = testJobGrossCost + testTaxes;

                      // Calculate complete price and cost per run
                      const testCompletePrice = testActualMaterialCost + testTotalJobCost;
                      const testCostPerRun = testCompletePrice / testRuns;

                      if (testCostPerRun < lowestCostPerRun) {
                        lowestCostPerRun = testCostPerRun;
                        optimalRuns = testRuns;
                      }
                    }

                    return { optimalRuns, optimalCost: lowestCostPerRun };
                  };

                  const maxRunsBPO = selectedNode.blueprint ? getMaxRunsBPO(selectedNode.blueprint) : 1;
                  const maxRunsBPC = selectedNode.blueprint?.maxProductionLimit ?? 1;
                  const { optimalRuns: optimalRunsBPO, optimalCost: optimalCostBPO } = calculateOptimalRuns(maxRunsBPO);
                  const { optimalRuns: optimalRunsBPC, optimalCost: optimalCostBPC } = calculateOptimalRuns(maxRunsBPC);

                  return (
                    <div className="space-y-6">
                      {/* Materials Header */}
                      <h2 className="text-xl font-semibold text-white">
                        {isReaction ? 'Reaction Inputs' : 'Input Materials'}
                      </h2>

                      {/* Build Price Summary */}
                      {outputProduct && productPrices && (
                        <div className="bg-gray-900 rounded-lg border border-gray-700 p-4">
                          <div className="text-sm text-gray-400 mb-3">Build Price vs Market ({outputProduct.type.name?.en})</div>
                          <div className="grid grid-cols-3 gap-4">
                            {/* Buy */}
                            <div className="text-center">
                              <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Janice Buy</div>
                              <div className="font-mono text-lg text-green-400">
                                {productPrices.buy !== undefined ? productPrices.buy.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '-'}
                              </div>
                              {productPrices.buy !== undefined && costPerItem > 0 && (
                                <div className={`text-sm font-mono mt-1 ${productPrices.buy > costPerItem ? 'text-green-400' : 'text-red-400'}`}>
                                  {productPrices.buy > costPerItem ? '+' : ''}{((productPrices.buy - costPerItem) / costPerItem * 100).toFixed(1)}%
                                  <span className="text-gray-500 ml-1">
                                    ({productPrices.buy > costPerItem ? '+' : ''}{(productPrices.buy - costPerItem).toLocaleString(undefined, { maximumFractionDigits: 0 })})
                                  </span>
                                </div>
                              )}
                            </div>
                            {/* Split */}
                            <div className="text-center">
                              <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Janice Split</div>
                              <div className="font-mono text-lg text-yellow-400">
                                {productPrices.split !== undefined ? productPrices.split.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '-'}
                              </div>
                              {productPrices.split !== undefined && costPerItem > 0 && (
                                <div className={`text-sm font-mono mt-1 ${productPrices.split > costPerItem ? 'text-green-400' : 'text-red-400'}`}>
                                  {productPrices.split > costPerItem ? '+' : ''}{((productPrices.split - costPerItem) / costPerItem * 100).toFixed(1)}%
                                  <span className="text-gray-500 ml-1">
                                    ({productPrices.split > costPerItem ? '+' : ''}{(productPrices.split - costPerItem).toLocaleString(undefined, { maximumFractionDigits: 0 })})
                                  </span>
                                </div>
                              )}
                            </div>
                            {/* Sell */}
                            <div className="text-center">
                              <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Janice Sell</div>
                              <div className="font-mono text-lg text-red-400">
                                {productPrices.sell !== undefined ? productPrices.sell.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '-'}
                              </div>
                              {productPrices.sell !== undefined && costPerItem > 0 && (
                                <div className={`text-sm font-mono mt-1 ${productPrices.sell > costPerItem ? 'text-green-400' : 'text-red-400'}`}>
                                  {productPrices.sell > costPerItem ? '+' : ''}{((productPrices.sell - costPerItem) / costPerItem * 100).toFixed(1)}%
                                  <span className="text-gray-500 ml-1">
                                    ({productPrices.sell > costPerItem ? '+' : ''}{(productPrices.sell - costPerItem).toLocaleString(undefined, { maximumFractionDigits: 0 })})
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="text-center mt-3 pt-3 border-t border-gray-700">
                            <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Build Cost per Item</div>
                            <div className="font-mono text-lg text-blue-400">
                              {costPerItem.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ISK
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Applied Modifiers and Action Buttons - side by side */}
                      <div className="flex gap-4 items-start">
                        {/* Material Modifiers Display - 50% width */}
                        <div className="flex-1 bg-gray-900 rounded-lg border border-gray-700 p-3">
                          <div className="text-xs text-gray-400 uppercase tracking-wider mb-2">Applied Modifiers</div>
                          <div className="grid grid-cols-1 gap-2 text-sm">
                            {!isReaction && nodeSettings.materialEfficiency !== 0 && (
                              <div className="text-gray-300">
                                Blueprint ME:
                                <span className={`ml-2 font-semibold ${nodeSettings.materialEfficiency > 0 ? 'text-green-400' : 'text-red-400'}`}>
                                  {nodeSettings.materialEfficiency > 0 ? '-' : '+'}{Math.abs(nodeSettings.materialEfficiency)}%
                                </span>
                              </div>
                            )}
                            {!isReaction && facility !== 'NPC' && (
                              <div className="text-gray-300">
                                Facility:
                                <span className="ml-2 font-semibold text-green-400">
                                  -{(getFacilityMaterialModifier(facility) * 100).toFixed(1)}%
                                </span>
                              </div>
                            )}
                            {effectiveMaterialRig !== 'None' && (
                              <div className="text-gray-300">
                                {isReaction ? 'Reaction' : 'Material'} Rig ({system}):
                                <span className="ml-2 font-semibold text-green-400">
                                  -{(getRigsMaterialModifier(effectiveMaterialRig, system) * 100).toFixed(2)}%
                                </span>
                              </div>
                            )}
                            {(isReaction ? effectiveMaterialRig === 'None' : nodeSettings.materialEfficiency === 0 && facility === 'NPC' && effectiveMaterialRig === 'None') && (
                              <div className="text-sm text-gray-500 italic">No modifiers applied</div>
                            )}
                          </div>
                        </div>

                        {/* Action Buttons - 50% width */}
                        <div className="flex-1 bg-gray-900 rounded-lg border border-gray-700 p-3">
                          <div className="text-xs text-gray-400 uppercase tracking-wider mb-2">Actions</div>
                          <div className="flex flex-wrap gap-2">
                            <button
                              onClick={() => fetchJanicePrices(details)}
                              disabled={refreshingPrices}
                              className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-500 text-white rounded flex items-center gap-2 disabled:opacity-50"
                            >
                              {refreshingPrices ? (
                                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                              ) : (
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                              )}
                              Refresh Prices
                            </button>
                            <button
                              onClick={async () => {
                                // Build all materials that can be built and aren't already set to build
                                for (const material of materials) {
                                  const buildableInfo = buildableCache[material.typeId];
                                  const currentDecision = buildDecisions[selectedNodeId]?.[material.typeId];
                                  const currentMode = currentDecision?.mode || 'buy';

                                  // Only build if material is buildable and not already in build mode
                                  if (buildableInfo && currentMode !== 'build') {
                                    await handleBuildModeChange(selectedNodeId, material.typeId, 'build', buildableInfo);
                                  }
                                }
                              }}
                              disabled={loadingSubBlueprints.size > 0}
                              className="px-3 py-1.5 text-sm bg-green-600 hover:bg-green-500 text-white rounded flex items-center gap-2 disabled:opacity-50"
                            >
                              {loadingSubBlueprints.size > 0 ? (
                                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                              ) : (
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                </svg>
                              )}
                              Build All
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Materials Table */}
                      <div className="bg-gray-900 rounded-lg border border-gray-700 overflow-hidden">
                        <table className="w-full">
                          <thead className="bg-gray-800">
                            <tr className="border-b border-gray-700">
                              <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider border-r border-gray-700 w-12">
                                #
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider border-r border-gray-700">
                                Material
                              </th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">Quantity</th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">Volume</th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">Price</th>
                              <th className="px-4 py-2 text-center text-xs font-medium text-gray-400 uppercase tracking-wider w-32">Build / Buy</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-700">
                            {materials.map((material, index) => {
                              const adjustedQuantity = calculateMaterialQuantity(
                                material.quantity,
                                nodeSettings.materialEfficiency,
                                nodeSettings.runs,
                                facility,
                                effectiveMaterialRig,
                                system,
                                isReaction
                              );
                              const janicePrice = getJanicePrice(material.typeId, nodeSettings.janicePriceType);
                              const volume = material.type.volume || 0;

                              // Check if this material can be built/reacted
                              const buildableInfo = buildableCache[material.typeId];
                              const canBuild = buildableInfo !== null && buildableInfo !== undefined;
                              const isBuildReaction = buildableInfo?.activity === 'reaction';

                              // Get current build decision for this material
                              const materialDecision = buildDecisions[selectedNodeId]?.[material.typeId];
                              const currentMode = materialDecision?.mode || 'buy';

                              // Calculate price based on build mode
                              let materialPrice: number | null = null;
                              let priceSource: 'janice' | 'build' = 'janice';

                              // Calculate Janice price for comparison (used for savings calculation)
                              const janiceTotalPrice = janicePrice !== null ? janicePrice * adjustedQuantity : null;

                              if (currentMode === 'build' && materialDecision?.childNodeId) {
                                // Get cost per unit from child node, then multiply by quantity needed
                                const childCostPerUnit = calculateChildNodeCostPerUnit(materialDecision.childNodeId);
                                if (childCostPerUnit !== null) {
                                  materialPrice = childCostPerUnit * adjustedQuantity;
                                  priceSource = 'build';
                                }
                              }

                              // Fallback to Janice price if no build cost available
                              if (materialPrice === null && janicePrice !== null) {
                                materialPrice = janiceTotalPrice;
                              }

                              // Calculate savings percentage when building
                              const buildSavingsPercent = (priceSource === 'build' && janiceTotalPrice !== null && materialPrice !== null && janiceTotalPrice > 0)
                                ? ((janiceTotalPrice - materialPrice) / janiceTotalPrice) * 100
                                : null;

                              // Check if loading this sub-blueprint
                              const loadingKey = `${selectedNodeId}-${material.typeId}`;
                              const isLoadingSubBlueprint = loadingSubBlueprints.has(loadingKey);

                              return (
                                <tr key={material.id} className="hover:bg-gray-800 transition-colors">
                                  <td className="px-2 py-2 text-left text-sm font-mono text-gray-400 border-r border-gray-700">
                                    {index + 1}
                                  </td>
                                  <td className="px-4 py-2 border-r border-gray-700">
                                    <div className="flex items-center gap-3">
                                      <img
                                        src={`https://images.evetech.net/types/${material.typeId}/icon?size=32`}
                                        alt={material.type.name?.en || 'Material'}
                                        className="w-8 h-8 rounded"
                                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                      />
                                      <div>
                                        <div className="text-sm font-medium text-white">
                                          {material.type.name?.en || `Type ${material.typeId}`}
                                        </div>
                                        <div className="text-xs text-gray-500">
                                          ID: {material.typeId}
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-4 py-2 text-right text-sm font-mono text-white">
                                    {adjustedQuantity.toLocaleString()}
                                  </td>
                                  <td className="px-4 py-2 text-right text-sm text-gray-300">
                                    {volume ? `${(volume * adjustedQuantity).toLocaleString()} m³` : '-'}
                                  </td>
                                  <td className="px-4 py-2 text-right text-sm text-gray-300">
                                    <div className="flex flex-col items-end">
                                      <span className={priceSource === 'build' ? 'text-green-400' : ''}>
                                        {materialPrice !== null
                                          ? `${materialPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ISK`
                                          : '-'}
                                      </span>
                                      {priceSource === 'build' && (
                                        <span className="text-xs">
                                          {buildSavingsPercent !== null && (
                                            <span className={buildSavingsPercent >= 0 ? 'text-green-400' : 'text-red-400'}>
                                              {buildSavingsPercent >= 0 ? '-' : '+'}{Math.abs(buildSavingsPercent).toFixed(1)}%
                                            </span>
                                          )}
                                          <span className="text-gray-500 ml-1">(built)</span>
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-4 py-2 text-center">
                                    {isLoadingSubBlueprint ? (
                                      <div className="flex justify-center">
                                        <svg className="animate-spin h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24">
                                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                      </div>
                                    ) : (
                                      <div className="inline-flex rounded-lg border border-gray-700 overflow-hidden">
                                        <button
                                          onClick={() => handleBuildModeChange(selectedNodeId, material.typeId, 'buy', buildableInfo)}
                                          className={`px-2 py-1 text-xs font-medium transition-colors ${
                                            currentMode === 'buy'
                                              ? 'bg-blue-600 text-white'
                                              : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                                          }`}
                                        >
                                          Buy
                                        </button>
                                        <button
                                          onClick={() => handleBuildModeChange(selectedNodeId, material.typeId, 'build', buildableInfo)}
                                          disabled={!canBuild}
                                          className={`px-2 py-1 text-xs font-medium transition-colors border-l border-gray-700 ${
                                            !canBuild
                                              ? 'bg-gray-800 text-gray-600 cursor-not-allowed'
                                              : currentMode === 'build'
                                                ? 'bg-green-600 text-white'
                                                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                                          }`}
                                          title={!canBuild ? 'No blueprint available' : (isBuildReaction ? 'React this material' : 'Build this material')}
                                        >
                                          {isBuildReaction ? 'React' : 'Build'}
                                        </button>
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                          <tfoot className="bg-gray-800 border-t border-gray-700">
                            <tr>
                              <td className="px-2 py-2 border-r border-gray-700"></td>
                              <td className="px-4 py-2 text-sm font-semibold text-white border-r border-gray-700">Total</td>
                              <td className="px-4 py-2"></td>
                              <td className="px-4 py-2 text-right text-sm font-semibold text-white">
                                {totalVolume.toLocaleString()} m³
                              </td>
                              <td className="px-4 py-2 text-right text-sm font-semibold text-white">
                                {totalPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ISK
                              </td>
                              <td className="px-4 py-2"></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>

                      {/* Job Costs and Analytics */}
                      <div className="flex gap-6">
                        {/* Job Costs */}
                        <div className="flex-1 bg-gray-900 rounded-lg border border-gray-700 p-4">
                          <h3 className="text-lg font-semibold text-white mb-3">Job Costs</h3>
                          <div className="space-y-3">
                            {/* EIV */}
                            <div className="flex items-center justify-between pb-2 border-b border-gray-700">
                              <span className="text-sm text-gray-400">EIV (Estimated Item Value):</span>
                              <span className="text-base font-mono font-semibold text-blue-400">
                                {eiv.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ISK
                              </span>
                            </div>

                            {/* System Cost */}
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-400">
                                System Cost (EIV × {(systemCostIndex * 100).toFixed(2)}%):
                              </span>
                              <span className="font-mono text-gray-300">
                                {systemCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ISK
                              </span>
                            </div>

                            {/* Structure Role Bonus */}
                            {getStructureRoleBonus() > 0 && (
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-400">
                                  Structure Role Bonus ({(getStructureRoleBonus() * 100).toFixed(0)}%):
                                </span>
                                <span className="font-mono text-green-400">
                                  -{structureRoleBonus.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ISK
                                </span>
                              </div>
                            )}

                            {/* Job Gross Cost */}
                            <div className="flex items-center justify-between text-sm font-medium pb-2 border-b border-gray-700">
                              <span className="text-gray-300">Job Gross Cost:</span>
                              <span className="font-mono text-yellow-400">
                                {jobGrossCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ISK
                              </span>
                            </div>

                            {/* Facility Tax */}
                            {facilityTax > 0 && (
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-400">
                                  Facility Tax ({((selectedStructure?.facilityTax ?? 0) * 100).toFixed(1)}%):
                                </span>
                                <span className="font-mono text-gray-300">
                                  {facilityTax.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ISK
                                </span>
                              </div>
                            )}

                            {/* SCC Surcharge */}
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-400">SCC Surcharge (4%):</span>
                              <span className="font-mono text-gray-300">
                                {sccSurcharge.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ISK
                              </span>
                            </div>

                            {/* Total Taxes */}
                            <div className="flex items-center justify-between text-sm font-medium pb-2 border-b border-gray-700">
                              <span className="text-gray-300">Total Taxes:</span>
                              <span className="font-mono text-orange-400">
                                {taxes.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ISK
                              </span>
                            </div>

                            {/* Total Job Cost */}
                            <div className="flex items-center justify-between pt-2">
                              <span className="text-base font-semibold text-white">Total Job Cost:</span>
                              <span className="text-xl font-mono font-bold text-green-400">
                                {totalJobCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ISK
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Analytics */}
                        <div className="flex-1 bg-gray-900 rounded-lg border border-gray-700 p-4">
                          <h3 className="text-lg font-semibold text-white mb-3">Analytics</h3>
                          <div className="space-y-4">
                            {/* Complete Price */}
                            <div className="pb-3 border-b border-gray-700">
                              <div className="text-sm text-gray-400 mb-1">Complete Price</div>
                              <div className="text-sm text-gray-500 mb-2">(Materials + Job Costs)</div>
                              <div className="text-2xl font-mono font-bold text-purple-400">
                                {completePrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ISK
                              </div>
                            </div>

                            {/* Price per Run */}
                            <div className="pb-3 border-b border-gray-700">
                              <div className="text-sm text-gray-400 mb-1">Price per Run</div>
                              <div className="text-sm text-gray-500 mb-2">(Complete Price ÷ {nodeSettings.runs} runs)</div>
                              <div className="text-xl font-mono font-semibold text-blue-400">
                                {pricePerRun.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ISK
                              </div>
                            </div>

                            {/* Cost per Item */}
                            {outputProduct && (
                              <div className="pb-3 border-b border-gray-700">
                                <div className="text-sm text-gray-400 mb-1">Cost per Item</div>
                                <div className="text-sm text-gray-500 mb-2">(Price per Run ÷ {outputQuantity} output)</div>
                                <div className="text-lg font-mono font-semibold text-cyan-400">
                                  {costPerItem.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ISK
                                </div>
                              </div>
                            )}

                            {/* Optimization */}
                            {!isReaction && (
                              <div>
                                <div className="text-sm text-gray-400 mb-3">Optimization</div>
                                <div className="grid grid-cols-2 gap-3">
                                  {/* BPO Optimization */}
                                  <div className="bg-gray-800 rounded-lg border border-gray-600 p-3">
                                    <div className="text-xs font-semibold text-blue-400 mb-2">BPO (Max: {maxRunsBPO.toLocaleString()})</div>
                                    {(() => {
                                      const savingsPercentage = ((pricePerRun - optimalCostBPO) / pricePerRun) * 100;
                                      if (savingsPercentage <= 0.01 || optimalRunsBPO === nodeSettings.runs) {
                                        return (
                                          <div className="p-2 bg-green-900 bg-opacity-30 border border-green-700 rounded text-xs text-green-400 text-center">
                                            ✓ Already optimal
                                          </div>
                                        );
                                      }
                                      return (
                                        <div className="space-y-2">
                                          <div className="text-xs text-gray-400">Optimal: <span className="text-yellow-400 font-semibold">{optimalRunsBPO.toLocaleString()}</span></div>
                                          <div className="text-xs text-gray-400">Cost/Run: <span className="text-green-400 font-semibold">{optimalCostBPO.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ISK</span></div>
                                          <div className="mt-2 p-2 bg-yellow-900 bg-opacity-30 border border-yellow-700 rounded text-xs text-yellow-400">
                                            💡 Saves {savingsPercentage.toFixed(2)}%
                                          </div>
                                        </div>
                                      );
                                    })()}
                                  </div>

                                  {/* BPC Optimization */}
                                  <div className="bg-gray-800 rounded-lg border border-gray-600 p-3">
                                    <div className="text-xs font-semibold text-purple-400 mb-2">BPC (Max: {maxRunsBPC.toLocaleString()})</div>
                                    {(() => {
                                      const savingsPercentage = ((pricePerRun - optimalCostBPC) / pricePerRun) * 100;
                                      if (savingsPercentage <= 0.01 || optimalRunsBPC === nodeSettings.runs) {
                                        return (
                                          <div className="p-2 bg-green-900 bg-opacity-30 border border-green-700 rounded text-xs text-green-400 text-center">
                                            ✓ Already optimal
                                          </div>
                                        );
                                      }
                                      return (
                                        <div className="space-y-2">
                                          <div className="text-xs text-gray-400">Optimal: <span className="text-yellow-400 font-semibold">{optimalRunsBPC.toLocaleString()}</span></div>
                                          <div className="text-xs text-gray-400">Cost/Run: <span className="text-green-400 font-semibold">{optimalCostBPC.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ISK</span></div>
                                          <div className="mt-2 p-2 bg-yellow-900 bg-opacity-30 border border-yellow-700 rounded text-xs text-yellow-400">
                                            💡 Saves {savingsPercentage.toFixed(2)}%
                                          </div>
                                        </div>
                                      );
                                    })()}
                                  </div>
                                </div>
                              </div>
                            )}

                          </div>
                        </div>

                        {/* Job Duration */}
                        <div className="flex-1 bg-gray-900 rounded-lg border border-gray-700 p-4">
                          <h3 className="text-lg font-semibold text-white mb-3">Job Duration</h3>
                          <div className="space-y-4">
                            {/* Skill Controls */}
                            <div className="pb-3 border-b border-gray-700">
                              <div className="text-sm text-gray-400 mb-2">Skills</div>
                              <div className="flex gap-4">
                                <div className="flex items-center gap-2">
                                  <label className="text-xs text-gray-400">Industry:</label>
                                  <input
                                    type="number"
                                    min="0"
                                    max="5"
                                    value={nodeSettings.industrySkill}
                                    onChange={(e) => {
                                      const value = parseInt(e.target.value);
                                      if (!isNaN(value) && value >= 0 && value <= 5) {
                                        updateNodeSettings(selectedNodeId, { industrySkill: value });
                                      }
                                    }}
                                    className="w-12 px-2 py-1 bg-gray-800 border border-gray-700 rounded text-white text-center text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  />
                                </div>
                                <div className="flex items-center gap-2">
                                  <label className="text-xs text-gray-400">Adv. Industry:</label>
                                  <input
                                    type="number"
                                    min="0"
                                    max="5"
                                    value={nodeSettings.advancedIndustrySkill}
                                    onChange={(e) => {
                                      const value = parseInt(e.target.value);
                                      if (!isNaN(value) && value >= 0 && value <= 5) {
                                        updateNodeSettings(selectedNodeId, { advancedIndustrySkill: value });
                                      }
                                    }}
                                    className="w-12 px-2 py-1 bg-gray-800 border border-gray-700 rounded text-white text-center text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  />
                                </div>
                              </div>
                            </div>

                            {/* Base Time */}
                            <div className="pb-3 border-b border-gray-700">
                              <div className="text-sm text-gray-400 mb-1">Base Time</div>
                              <div className="text-xl font-mono text-white">
                                {formatDuration(baseTime)}
                              </div>
                            </div>

                            {/* Time Modifiers */}
                            <div className="pb-3 border-b border-gray-700">
                              <div className="text-sm text-gray-400 mb-2">Applied Modifiers</div>
                              <div className="space-y-1 text-sm">
                                {nodeSettings.timeEfficiency > 0 && (
                                  <div className="text-gray-300">
                                    Time Efficiency:
                                    <span className="ml-2 font-semibold text-green-400">
                                      -{(nodeSettings.timeEfficiency * 2)}%
                                    </span>
                                  </div>
                                )}
                                {(nodeSettings.industrySkill > 0 || nodeSettings.advancedIndustrySkill > 0) && (
                                  <div className="text-gray-300">
                                    Skill Modifiers:
                                    <span className="ml-2 font-semibold text-green-400">
                                      -{(nodeSettings.industrySkill * 4) + (nodeSettings.advancedIndustrySkill * 3)}%
                                    </span>
                                  </div>
                                )}
                                {facility !== 'NPC' && (
                                  <div className="text-gray-300">
                                    Facility Bonus:
                                    <span className="ml-2 font-semibold text-green-400">
                                      -{(getFacilityTimeModifier(facility) * 100).toFixed(1)}%
                                    </span>
                                  </div>
                                )}
                                {effectiveTimeRig !== 'None' && (
                                  <div className="text-gray-300">
                                    Time Rig Bonus ({system} sec):
                                    <span className="ml-2 font-semibold text-green-400">
                                      -{(getRigsTimeModifier(effectiveTimeRig, system) * 100).toFixed(2)}%
                                    </span>
                                  </div>
                                )}
                                {nodeSettings.timeEfficiency === 0 && nodeSettings.industrySkill === 0 && nodeSettings.advancedIndustrySkill === 0 && facility === 'NPC' && effectiveTimeRig === 'None' && (
                                  <div className="text-sm text-gray-500 italic">No modifiers applied</div>
                                )}
                              </div>
                            </div>

                            {/* Actual Time */}
                            <div>
                              <div className="text-sm text-gray-400 mb-1">Actual Time</div>
                              <div className={`text-xl font-mono ${actualTime !== baseTime ? 'text-green-400' : 'text-white'}`}>
                                {formatDuration(actualTime)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center text-gray-500">
              Select a node to view details
            </div>
          )}
        </div>

        {/* Add Blueprint Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-gray-800 rounded-lg w-[600px] max-h-[80vh] flex flex-col border border-gray-700">
              {/* Modal Header */}
              <div className="p-4 border-b border-gray-700 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-white">Add Blueprint</h2>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setAddingToNodeId(null);
                  }}
                  className="text-gray-400 hover:text-white"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Search */}
              <div className="p-4 border-b border-gray-700">
                <input
                  type="text"
                  placeholder="Type 3+ chars to search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                />
                <div className="mt-2 text-xs text-gray-500">
                  {searchLoading ? 'Loading...' : `${searchResults.length} blueprints`}
                  {searchQuery.length > 0 && searchQuery.length < 3 && (
                    <span className="text-yellow-500"> (need {3 - searchQuery.length} more char{3 - searchQuery.length > 1 ? 's' : ''})</span>
                  )}
                </div>
              </div>

              {/* Blueprint Cards */}
              <div className="flex-1 overflow-y-auto p-2 space-y-1">
                {searchLoading ? (
                  <div className="text-center text-gray-500 mt-8">Loading...</div>
                ) : searchResults.length === 0 ? (
                  <div className="text-center text-gray-500 mt-8">
                    {searchQuery.length === 0 ? 'Loading blueprints...' : searchQuery.length < 3 ? 'Type 3+ characters' : 'No blueprints found'}
                  </div>
                ) : (
                  searchResults.map((blueprint) => (
                    <button
                      key={blueprint.id}
                      onClick={() => setSelectedModalBlueprint(blueprint)}
                      className={`w-full rounded-lg border transition-all ${
                        selectedModalBlueprint?.id === blueprint.id
                          ? 'bg-blue-900 border-blue-500'
                          : 'bg-gray-700 border-gray-600 hover:bg-gray-650 hover:border-gray-500'
                      }`}
                    >
                      <div className="flex items-stretch gap-2">
                        <img
                          src={getBlueprintIcon(blueprint)}
                          alt={getBlueprintName(blueprint)}
                          className="w-12 h-12 rounded-l-lg flex-shrink-0 object-cover"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                        <div className="flex-1 text-left min-w-0 flex flex-col justify-center py-2 pr-2">
                          <div className="text-xs font-medium text-white truncate flex items-center gap-1">
                            {getBlueprintName(blueprint)}
                            {blueprint.activity === 'reaction' && (
                              <span className="px-1 py-0.5 text-[10px] bg-purple-600 text-white rounded">R</span>
                            )}
                          </div>
                          <div className="text-xs text-gray-400">
                            ID: {blueprint.blueprintTypeId}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>

              {/* Modal Footer */}
              <div className="p-4 border-t border-gray-700 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setAddingToNodeId(null);
                  }}
                  className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateNode}
                  disabled={!selectedModalBlueprint}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    selectedModalBlueprint
                      ? 'bg-blue-600 text-white hover:bg-blue-500'
                      : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  Create
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default BuildTree;
